/**
 * WeaveIn.ai Platform - Member Field Configuration Manager
 * Admin UI to customize member profile field display names, visibility, and categories
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  FolderOpen,
  Settings2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  X,
  AlertCircle,
  Layout,
  Pin,
  ArrowRight,
  Layers,
  RefreshCw,
  GripVertical,
  Download,
  Upload,
  FileSpreadsheet,
  Columns,
  Search,
  Filter,
  Tag,
} from 'lucide-react';
import { exportFieldConfigToCSV } from '@/utils/fieldConfigExport';
import {
  DndContext,
  closestCenter,
  closestCorners,
  pointerWithin,
  rectIntersection,
  getFirstCollision,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
  useDroppable,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ApiErrorAlert } from '../common/ApiErrorAlert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  membersAPI,
  CoreField,
  CustomField,
  FieldCategory,
  FieldCategoryCreate,
  FieldConfiguration,
  FieldConfigurationCreate,
  FieldConfigurationUpdate,
  FieldOption,
  ProfileField,
  RECORD_TYPE_SCOPES,
  recordTypeScopeLabel,
  RecordTypeScope,
  UnifiedViewSettings,
  ProfileLayout,
  ProfileLayoutTab,
  ProfileLayoutOverrideInput,
  ProfilePlacement,
  DisplayWidth,
} from '@/services/api/members';
import { programsAPI, type Program } from '@/services/api/programs';

import { useAppTranslation } from '@/contexts/TranslationContext';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import FieldConfigManager, { StandardFieldRow } from './FieldConfigManager';
import FieldGroupsManager from './FieldGroupsManager';
import FieldMappingManager from './FieldMappingManager';
import ProgramFieldOverridesManager from './ProgramFieldOverridesManager';
import FieldStudio from './FieldStudio';
import PinnedFormsManager from './PinnedFormsManager';
import { useMemberProfileV2 } from '@/components/members/memberProfileRollout';
import ProgramCompareCloneManager from './ProgramCompareCloneManager';
import FieldConfigAIAssistant from './FieldConfigAIAssistant';
import useFieldGroups from '@/hooks/useFieldGroups';

interface MemberFieldConfigManagerProps {
  onNavigate?: (tab: string) => void;
}

// Unified field type that can represent both core fields and custom fields
interface UnifiedField {
  id: number;
  field_key: string;
  display_name: string;
  field_type: string;
  field_group: string;
  is_system_field: boolean;
  sort_order: number;
  is_custom: boolean;
  config?: FieldConfiguration;
  // Per-cbo record-type scope (TRUS-100 part 1). Threaded from the
  // configuration row's applies_to_record_types. undefined/null = inherit
  // (the field is treated as applying to both members and contacts).
  applies_to_record_types?: RecordTypeScope[] | null;
  // Custom field specific properties
  mandatory?: boolean;
  hidden?: boolean;
  category?: string;
}

const DISPLAY_WIDTHS: { value: DisplayWidth; label: string }[] = [
  { value: 'full', label: 'Full Width' },
  { value: 'half', label: 'Half Width' },
  { value: 'third', label: 'One Third' },
  { value: 'quarter', label: 'One Quarter' },
];

/**
 * TRUS-394: this screen had grown to a single row of eleven tabs mixing two
 * unrelated jobs — looking at how fields will appear, and building the field
 * configuration itself. They're split into two groups so the tab bar reads as
 * "what do I want to do" first, "which surface" second.
 *
 * The active group is DERIVED from the active tab rather than stored, so the
 * existing deep links that call setActiveConfigTab('field-groups') directly
 * land on the right group with no extra bookkeeping.
 */
const TAB_GROUPS: { id: string; label: string; hint: string; tabs: { value: string; label: string }[] }[] = [
  {
    id: 'views',
    label: 'Views',
    hint: 'See how fields are laid out and where they live.',
    tabs: [
      { value: 'profile-builder', label: 'Profile Builder' },
      { value: 'profile-layout', label: 'Profile Layout' },
      { value: 'unified', label: 'Unified View' },
      { value: 'all-fields', label: 'All Fields' },
      { value: 'field-studio', label: 'Field Studio' },
    ],
  },
  {
    id: 'configure',
    label: 'Configure',
    hint: 'Define fields, groups and categories.',
    tabs: [
      { value: 'field-groups', label: 'Field Groups' },
      { value: 'custom-fields', label: 'Field Builder' },
      { value: 'categories', label: 'Categories' },
      { value: 'field-mappings', label: 'Field Mappings' },
      { value: 'ai-assistant', label: 'AI Assistant' },
      { value: 'program-overrides', label: 'Program Overrides' },
      { value: 'compare-clone', label: 'Compare & Clone' },
    ],
  },
];

/**
 * Toggle rows for the member-profile tabs, derived from the SERVER's nav tree
 * (GET /profile-layout, from app/services/member_profile/tab_registry.py).
 *
 * This used to be a hand-written PROFILE_TAB_SECTIONS literal restating the
 * tree that MemberProfileNew.tsx renders. The two had already drifted:
 * `show_billing` was missing entirely, so no org could switch the
 * "Billing & Reimbursement" tab off even though the column, the renderer and
 * the program override all existed; `show_external_referrals` was missing too,
 * and four keys it did list (identifiers / milestones / signatures /
 * assessments) are read by nothing. Deriving the rows means a new tab shows up
 * here the moment the server knows about it.
 *
 * One row per settings column, not per element: several elements can share a
 * column (Conversations and Outreach are both `show_communications`). The row
 * is labelled by the element that owns the column ALONE — so
 * `show_coordination` reads "Care Coordination" (its sub-tab) rather than
 * "Referrals & Care Coordination" (the parent tab, which spans two columns).
 */
export function deriveTabToggleSections(
  layout: ProfileLayout | null,
): { section: string; tabs: { key: string; label: string }[] }[] {
  if (!layout) return [];

  const owner = new Map<string, { label: string; section: string }>();
  const fallback = new Map<string, { label: string; section: string }>();

  for (const section of layout.sections) {
    for (const tab of section.tabs.flatMap((t) => [t, ...(t.sub_tabs ?? [])])) {
      for (const column of tab.settings_columns) {
        const entry = { label: tab.label, section: section.label };
        if (tab.settings_columns.length === 1) {
          if (!owner.has(column)) owner.set(column, entry);
        } else if (!fallback.has(column)) {
          fallback.set(column, entry);
        }
      }
    }
  }

  const bySection = new Map<string, { key: string; label: string }[]>();
  const order: string[] = [];
  for (const section of layout.sections) {
    order.push(section.label);
    bySection.set(section.label, []);
  }
  // Sole-owner wins; a column only ever claimed by multi-column elements falls
  // back to the first of those. Each column yields exactly one row.
  for (const column of new Set([...owner.keys(), ...fallback.keys()])) {
    const entry = owner.get(column) ?? fallback.get(column);
    if (!entry) continue;
    const rows = bySection.get(entry.section);
    if (!rows || rows.some((r) => r.key === column)) continue;
    rows.push({ key: column, label: entry.label });
  }

  return order
    .map((label) => ({ section: label, tabs: bySection.get(label) ?? [] }))
    .filter((s) => s.tabs.length > 0);
}

// Tri-state override value for a program tab.
type OverrideState = 'inherit' | 'show' | 'hide';

const FIELD_GROUP_ICONS: Record<string, string> = {
  identity: '👤',
  demographics: '📊',
  contact: '📱',
  address: '📍',
  insurance: '🛡️',
  family_member: '👨‍👩‍👧',
  household: '🏠',
  caregiver: '❤️',
  custom: '⚙️',
};

// Available emojis for category icons
const CATEGORY_EMOJI_OPTIONS = [
  { value: '📁', label: 'Folder' },
  { value: '📋', label: 'Clipboard' },
  { value: '👤', label: 'Person' },
  { value: '👥', label: 'People' },
  { value: '📊', label: 'Chart' },
  { value: '📱', label: 'Phone' },
  { value: '📍', label: 'Location' },
  { value: '🏠', label: 'Home' },
  { value: '🏥', label: 'Hospital' },
  { value: '🛡️', label: 'Shield' },
  { value: '💼', label: 'Briefcase' },
  { value: '📝', label: 'Note' },
  { value: '📅', label: 'Calendar' },
  { value: '⚙️', label: 'Settings' },
  { value: '🔧', label: 'Wrench' },
  { value: '💰', label: 'Money' },
  { value: '🎯', label: 'Target' },
  { value: '⭐', label: 'Star' },
  { value: '❤️', label: 'Heart' },
  { value: '🔔', label: 'Bell' },
  { value: '📧', label: 'Email' },
  { value: '🔒', label: 'Lock' },
  { value: '🎓', label: 'Education' },
  { value: '🚗', label: 'Car' },
  { value: '👶', label: 'Baby' },
  { value: '🩺', label: 'Medical' },
  { value: '💊', label: 'Medicine' },
  { value: '🏢', label: 'Building' },
  { value: '📞', label: 'Telephone' },
  { value: '🌐', label: 'Globe' },
];

// Field types that carry an editable allowed-value list (TRUS-100 part 4).
// enum = core fields (gender/race/ethnicity/preferred_language); select/radio
// = custom fields with a fixed option set.
const OPTION_FIELD_TYPES = new Set(['enum', 'select', 'radio', 'multiselect', 'dropdown']);
function fieldSupportsOptions(fieldType?: string): boolean {
  return !!fieldType && OPTION_FIELD_TYPES.has(fieldType.toLowerCase());
}

// Guard the allowed-values editor against a non-array options value (TRUS-610):
// the editor renders `field_options.map(...)` and the `|| []` guard only covers
// null/undefined, so any truthy non-array (e.g. the keyed dict the
// /field-options endpoint returns) crashes the dialog. Coerce an unexpected
// non-array to an empty array. `null` is preserved so the save path keeps its
// "inherit / don't clobber the column" semantics.
function normalizeFieldOptions(value: unknown): FieldOption[] | null {
  if (value == null) return null;
  return Array.isArray(value) ? (value as FieldOption[]) : [];
}

// Payload the row's red "Remove from category" (X) action sends to clear a
// field's category and return it to the default group (TRUS-474). The category
// must be cleared with an explicit `null`: sending `undefined` (the previous
// behavior) is silently dropped by JSON.stringify, so the PUT body arrives empty
// and the backend rejects it with 400 "No fields to update" — which surfaced as
// "clicking the X does nothing / shows an error". Exported for regression cover.
export function buildClearCategoryPayload(): FieldConfigurationUpdate {
  return { category_id: null };
}

// Sortable Field Item Component for drag and drop
interface SortableFieldItemProps {
  field: UnifiedField;
  categoryKey: string;
  isHidden: boolean;
  onToggleVisibility: () => void;
  showInList: boolean;
  onToggleShowInList: () => void;
  // Unified list/filter/chip framework — opt-in. When the parent passes
  // these handlers, the row exposes the corresponding toggle.
  showInAdvancedFilter?: boolean;
  onToggleShowInAdvancedFilter?: () => void;
  showAsQuickFilter?: boolean;
  onToggleShowAsQuickFilter?: () => void;
  onEdit: () => void;
  onRemoveFromCategory?: () => void;
  onAssignToCategory?: () => void;
  canAssign: boolean;
  canRemove: boolean;
  disabled?: boolean;
}

function SortableFieldItem({
  field,
  categoryKey,
  isHidden,
  onToggleVisibility,
  showInList,
  onToggleShowInList,
  showInAdvancedFilter,
  onToggleShowInAdvancedFilter,
  showAsQuickFilter,
  onToggleShowAsQuickFilter,
  onEdit,
  onRemoveFromCategory,
  onAssignToCategory,
  canAssign,
  canRemove,
  disabled,
}: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `${categoryKey}:${field.field_key}`,
    disabled: disabled, // Enable drag for both core and custom fields
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // Whole-row drag handle. Users complained the 16px GripVertical was too
      // small to grab reliably (TRUS-69), so attributes/listeners now live on
      // the outer row. PointerSensor's `distance: 8` activation constraint
      // means a quick click on action buttons still registers as a click, not
      // a drag, so the action buttons keep working unchanged.
      {...attributes}
      {...listeners}
      className={`flex items-center gap-3 p-3 cursor-grab active:cursor-grabbing select-none ${
        isHidden ? 'opacity-50 bg-muted/20' : ''
      } hover:bg-muted/30 transition-colors ${isDragging ? 'bg-primary/10 shadow-lg z-50' : ''}`}
    >
      {/* Drag affordance — visual hint that the whole row is draggable. */}
      <div className="p-1 rounded text-muted-foreground" aria-hidden="true">
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Field Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{field.display_name}</span>
          {field.is_custom && (
            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">Custom Field</Badge>
          )}
          {field.is_system_field && (
            <Badge variant="outline" className="text-xs">System</Badge>
          )}
          {isHidden && (
            <Badge variant="secondary" className="text-xs gap-1">
              <EyeOff className="h-3 w-3" />
              Hidden
            </Badge>
          )}
          {showInList && (
            <Badge variant="secondary" className="text-xs gap-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <Columns className="h-3 w-3" />
              List column
            </Badge>
          )}
          {(field.is_custom ? field.mandatory : field.config?.is_required) && (
            <Badge variant="destructive" className="text-xs">Required</Badge>
          )}
          {/* Record-type scope (TRUS-100). Always show the scope so users can
              see and trust it at a glance — including the default
              "Members + Contacts". Previously this was suppressed for the
              default, which left users unable to tell a field's scope. */}
          <Badge variant="outline" className="text-xs gap-1">
            <Tag className="h-3 w-3" />
            {recordTypeScopeLabel(field.applies_to_record_types)}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <code className="bg-muted px-1 rounded">{field.field_key}</code>
          <span>•</span>
          <span className="capitalize">{field.field_type}</span>
          {!field.is_custom && field.config?.display_width && field.config.display_width !== 'full' && (
            <>
              <span>•</span>
              <span>{field.config.display_width} width</span>
            </>
          )}
          {field.is_custom && field.category && (
            <>
              <span>•</span>
              <span>Category: {field.category}</span>
            </>
          )}
        </div>
      </div>

      {/* Field Actions */}
      <div className="flex items-center gap-1">
        {/* Show-in-list toggle — works for both core and custom fields so each
            org can choose which fields are columns in the member list view. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleShowInList}
          disabled={disabled}
          title={showInList ? 'Remove from member list columns' : 'Show as a column in the member list'}
          className={showInList ? 'text-green-600 dark:text-green-400' : ''}
        >
          <Columns className="h-4 w-4" />
        </Button>
        {onToggleShowInAdvancedFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleShowInAdvancedFilter}
            disabled={disabled}
            title={showInAdvancedFilter
              ? 'Remove from Advanced Filters dropdown'
              : 'Show as an Advanced Filter input on the member list'}
            className={showInAdvancedFilter ? 'text-blue-600 dark:text-blue-400' : ''}
          >
            <Filter className="h-4 w-4" />
          </Button>
        )}
        {onToggleShowAsQuickFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleShowAsQuickFilter}
            disabled={disabled}
            title={showAsQuickFilter
              ? 'Remove from Quick Filter chips'
              : 'Surface as a one-click Quick Filter chip'}
            className={showAsQuickFilter ? 'text-purple-600 dark:text-purple-400' : ''}
          >
            <Tag className="h-4 w-4" />
          </Button>
        )}
        {!field.is_custom ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleVisibility}
              disabled={field.is_system_field || disabled}
              title={isHidden ? 'Show field' : 'Hide field'}
            >
              {isHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={field.is_system_field}
              title="Edit field settings"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {canRemove && onRemoveFromCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemoveFromCategory}
                disabled={disabled}
                title="Remove from category (return to default group)"
              >
                <X className="h-4 w-4 text-destructive" />
              </Button>
            )}
            {canAssign && onAssignToCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onAssignToCategory}
                disabled={disabled}
                title="Assign to category"
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </>
        ) : (
          // Custom fields can be edited via the Custom Fields tab
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            title="Edit custom field settings"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Droppable Category Container
interface DroppableCategoryProps {
  categoryKey: string;
  children: React.ReactNode;
  isOver?: boolean;
}

function DroppableCategory({ categoryKey, children, isOver }: DroppableCategoryProps) {
  const { setNodeRef } = useDroppable({
    id: categoryKey,
  });

  return (
    <div
      ref={setNodeRef}
      className={`divide-y divide-border min-h-[60px] transition-colors ${
        isOver ? 'bg-primary/10 ring-2 ring-primary ring-inset' : ''
      }`}
    >
      {children}
    </div>
  );
}

export default function MemberFieldConfigManager({ onNavigate: _onNavigate }: MemberFieldConfigManagerProps) {
  // TRUS-762 rollout: pinned forms belong to the new member profile.
  const { enabled: memberProfileV2 } = useMemberProfileV2();
  const { t } = useAppTranslation();

  // State
  // Which record type the manager is currently configuring fields for.
  // Both members and contacts live in the `members` table; the scope is
  // honored via `applies_to_record_types` on the field/config rows.
  // Initial scope can be preset by another page (e.g. Contacts -> "Configure
  // contact fields") via localStorage["field_config_initial_scope"].
  // Persist the chosen scope across page loads. One-shot localStorage HINT
  // (used when another page deep-links us — e.g. Contacts → "Configure
  // contact fields") wins, then the user's last-used scope (sticky), then
  // 'member' as the platform default.
  // All Fields tab: every custom field in the org regardless of record type. Loaded
  // without a record_type filter (and with group children included) so it is a true
  // master list rather than whatever the current scope happens to show.
  const [masterFields, setMasterFields] = useState<CustomField[]>([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterScope, setMasterScope] = useState<'all' | RecordTypeScope>('all');
  const [masterQuery, setMasterQuery] = useState('');

  // Every load below is scoped to this org via the X-CBO-ID header, so it must
  // be a dependency of those loads — see loadDataInternal.
  const activeOrgId = useActiveOrgId();

  const [recordScope, setRecordScope] = useState<RecordTypeScope>(() => {
    if (typeof window === 'undefined') return 'member';
    const hint = window.localStorage.getItem('field_config_initial_scope');
    if (hint === 'contact' || hint === 'member' || hint === 'activity_event' || hint === 'family_member') {
      window.localStorage.removeItem('field_config_initial_scope');
      return hint;
    }
    const sticky = window.localStorage.getItem('field_config_last_scope');
    if (sticky === 'contact' || sticky === 'member' || sticky === 'activity_event' || sticky === 'family_member') {
      return sticky;
    }
    return 'member';
  });

  // Sticky-write the scope whenever it changes so the next visit lands on
  // the same tab. Prevents the "I'm on Somos, nothing shows" trap: their
  // 46 custom fields are all activity_event-scoped, so the default 'member'
  // tab is empty.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('field_config_last_scope', recordScope);
    }
  }, [recordScope]);
  const [coreFields, setCoreFields] = useState<CoreField[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [categories, setCategories] = useState<FieldCategory[]>([]);
  const [configurations, setConfigurations] = useState<FieldConfiguration[]>([]);
  const [unifiedSettings, setUnifiedSettings] = useState<UnifiedViewSettings | null>(null);
  // The server's nav tree — what tabs exist, this org's wording and order, and
  // which settings column gates each one. Drives both the visibility toggles
  // below and the Profile Layout editor. (TRUS-528)
  const [profileLayout, setProfileLayout] = useState<ProfileLayout | null>(null);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const tabToggleSections = useMemo(() => deriveTabToggleSections(profileLayout), [profileLayout]);

  // ── Program-scoped tab-visibility overrides ──────────────────────
  // Org-default toggles (above) vs per-program overrides (tri-state).
  const [visibilityMode, setVisibilityMode] = useState<'org' | 'program'>('org');
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [programOverrides, setProgramOverrides] = useState<Record<string, boolean>>({});
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [savingOverrideKey, setSavingOverrideKey] = useState<string | null>(null);

  // Field groups (repeatable groups of custom fields). They appear as their own
  // section in the Profile Builder; their child fields are hidden from the loose
  // category listing so they don't double-show.
  const { groups: fieldGroups, refetch: refetchFieldGroups } = useFieldGroups();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showFieldConfigDialog, setShowFieldConfigDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FieldCategory | null>(null);
  const [editingConfig, setEditingConfig] = useState<FieldConfiguration | null>(null);
  const [selectedCoreField, setSelectedCoreField] = useState<CoreField | null>(null);
  // When editing a CUSTOM field's config, the custom field id (else null for
  // core fields). Lets the same dialog configure both.
  const [editingCustomFieldId, setEditingCustomFieldId] = useState<number | null>(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState<FieldCategoryCreate>({
    category_key: '',
    display_name: '',
    description: '',
    icon: '',
    sort_order: 0,
    is_collapsed_by_default: false,
  });

  const [configForm, setConfigForm] = useState<FieldConfigurationCreate>({
    core_field_key: '',
    display_name: '',
    placeholder_text: '',
    help_text: '',
    is_hidden: false,
    is_required: false,
    is_readonly: false,
    sort_order: 0,
    display_width: 'full',
    applies_to_record_types: null,
  });

  // Profile Builder state
  const [expandedBuilderCategories, setExpandedBuilderCategories] = useState<Set<string>>(new Set());
  // Active top-level Tabs value — controlled so we can switch tabs from
  // child UI (e.g. clicking "Manage members" on a field-group section
  // routes the admin to the Field Groups tab and pre-opens the children
  // editor for that group). TRUS-77.
  const [activeConfigTab, setActiveConfigTab] = useState<string>('profile-builder');
  // Derived, not stored — see TAB_GROUPS. Falls back to the first group so an
  // unknown tab value can never leave the tab bar with nothing selected.
  const activeTabGroup =
    TAB_GROUPS.find((g) => g.tabs.some((t) => t.value === activeConfigTab)) ?? TAB_GROUPS[0];
  const [focusFieldGroupId, setFocusFieldGroupId] = useState<number | null>(null);
  // Profile Builder filter: free-text search + a status facet so admins can
  // find which fields are visible / hidden / already in the member list columns.
  const [builderSearch, setBuilderSearch] = useState('');
  const [builderFilter, setBuilderFilter] = useState<'all' | 'in_list' | 'hidden' | 'visible' | 'configured'>('all');
  const [selectedFieldForAssignment, setSelectedFieldForAssignment] = useState<CoreField | UnifiedField | null>(null);
  const [showAssignFieldDialog, setShowAssignFieldDialog] = useState(false);
  // TRUS-100 part 3: read-only "Preview" mode that mirrors the member/contact
  // profile layout (sections in order, fields under each) via the same
  // /profile-fields endpoint the member profile renders from.
  const [builderPreview, setBuilderPreview] = useState(false);
  const [previewFields, setPreviewFields] = useState<ProfileField[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [syncingCategories, setSyncingCategories] = useState(false);

  // Drag and drop state
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px drag threshold before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Collision strategy that prefers sortable field IDs (those formatted as
   * "categoryKey:fieldKey") over the parent category container ID. With the
   * default `closestCenter` strategy, dragging a field UPWARD often resolves
   * to the source category's own wrapper instead of the field above — which
   * triggered handleDragEnd's "same category container, no-op" branch and made
   * the drop appear to fail (TRUS-59 #2).
   *
   * Pipeline:
   *   1. `pointerWithin` — find every droppable under the pointer.
   *   2. If any of them is a field (id contains ':'), prefer the closest one.
   *   3. Otherwise fall back to `closestCorners` which behaves more sensibly
   *      than `closestCenter` for vertical sortable lists.
   */
  const fieldFirstCollisionDetection: CollisionDetection = useCallback((args) => {
    const pointer = pointerWithin(args);
    const fieldHit = pointer.find((c) => String(c.id).includes(':'));
    if (fieldHit) return [fieldHit];
    if (pointer.length > 0) return pointer;
    const intersections = rectIntersection(args);
    const fieldIntersection = intersections.find((c) => String(c.id).includes(':'));
    if (fieldIntersection) return [fieldIntersection];
    return closestCorners(args);
  }, []);

  // Compute unified view of categories with their assigned fields
  const unifiedCategoryView = useMemo(() => {
    // Get all categories (custom + default groups)
    const defaultGroups = ['identity', 'demographics', 'contact', 'address', 'insurance', 'custom'];

    // Build a map of category_id to fields
    const customCategoryFields = new Map<number, UnifiedField[]>();
    const defaultGroupFields = new Map<string, UnifiedField[]>();

    // Initialize default groups
    defaultGroups.forEach(group => defaultGroupFields.set(group, []));

    // Initialize custom categories
    categories.forEach(cat => customCategoryFields.set(cat.id, []));

    // Convert core field to unified field
    const coreToUnified = (field: CoreField, config?: FieldConfiguration): UnifiedField => ({
      id: field.id,
      field_key: field.field_key,
      display_name: config?.display_name || field.default_display_name,
      field_type: field.field_type,
      field_group: field.field_group,
      is_system_field: field.is_system_field,
      sort_order: config?.sort_order ?? field.sort_order,
      is_custom: false,
      config,
      applies_to_record_types: config?.applies_to_record_types ?? null,
    });

    // Convert custom field to unified field
    const customToUnified = (field: CustomField): UnifiedField => ({
      id: field.id,
      field_key: `custom_${field.name}`,
      display_name: field.friendly_name,
      field_type: field.type,
      field_group: field.category || 'custom',
      is_system_field: false,
      sort_order: 1000 + field.id, // Custom fields come after core fields
      is_custom: true,
      mandatory: field.mandatory,
      hidden: field.hidden,
      category: field.category,
    });

    // Assign each core field to its category
    coreFields.forEach(field => {
      const config = configurations.find(
        c => c.field_key === field.field_key || coreFields.find(cf => cf.id === c.core_field_id)?.field_key === field.field_key
      );
      const unifiedField = coreToUnified(field, config);

      if (config?.category_id) {
        // Field has custom category assignment
        const existing = customCategoryFields.get(config.category_id) || [];
        existing.push(unifiedField);
        customCategoryFields.set(config.category_id, existing);
      } else {
        // Field uses default group
        const existing = defaultGroupFields.get(field.field_group) || [];
        existing.push(unifiedField);
        defaultGroupFields.set(field.field_group, existing);
      }
    });

    // Custom fields belonging to a field group are surfaced through the group
    // section, so exclude them from the regular category listing. Built once
    // here so both the categorized and "unsynced" branches honor it.
    const customFieldIdsInGroups = new Set<number>(
      (fieldGroups || []).flatMap((g) => g.children.map((c) => c.field_id))
    );

    // Group custom fields - either into matching FieldCategory or separate unsynced groups
    const customFieldsByCategory = new Map<string, UnifiedField[]>();
    const customFieldsInCategories = new Map<number, UnifiedField[]>(); // For fields matching FieldCategory

    customFields.forEach(field => {
      if (customFieldIdsInGroups.has(field.id)) return; // Skip — owned by a field group
      // Check if there's a FieldConfiguration for this custom field with a category_id
      const fieldConfig = configurations.find(c => c.custom_field_id === field.id);
      const unifiedField = {
        ...customToUnified(field),
        config: fieldConfig, // Attach the config to the unified field
        // Honor the per-org configured display name. customToUnified falls back
        // to the raw friendly_name, so without this the list kept showing the
        // default label even after a Display Name override was saved — the edit
        // looked like it "didn't save" (core fields already did this via
        // coreToUnified). (TRUS-365)
        display_name: fieldConfig?.display_name || field.friendly_name,
        applies_to_record_types: fieldConfig?.applies_to_record_types ?? null,
        // Honor the configured/drag-saved sort order. Without this, custom
        // fields fell back to customToUnified's 1000+id default, so the
        // category re-sorted by creation order and any drag-to-reorder of a
        // custom field snapped right back (root cause: builder ignored the
        // saved sort_order for custom fields — core fields already used it).
        sort_order: fieldConfig?.sort_order ?? (1000 + field.id),
      };

      // If there's a configuration with category_id, use that
      if (fieldConfig?.category_id) {
        const existing = customFieldsInCategories.get(fieldConfig.category_id) || [];
        existing.push(unifiedField);
        customFieldsInCategories.set(fieldConfig.category_id, existing);
        return; // Don't process further
      }

      // Otherwise, use the field's native category
      const fieldCategory = field.category || 'Uncategorized';

      // Check if this category matches an existing FieldCategory (by display_name)
      const matchingCategory = categories.find(c =>
        c.display_name.toLowerCase() === fieldCategory.toLowerCase() ||
        c.category_key === fieldCategory.toLowerCase().replace(/\s+/g, '_')
      );

      if (matchingCategory) {
        // Add to the matching FieldCategory
        const existing = customFieldsInCategories.get(matchingCategory.id) || [];
        existing.push(unifiedField);
        customFieldsInCategories.set(matchingCategory.id, existing);
      } else {
        // Add to unsynced category group
        const existing = customFieldsByCategory.get(fieldCategory) || [];
        existing.push(unifiedField);
        customFieldsByCategory.set(fieldCategory, existing);
      }
    });

    // Build unified view: custom categories first (sorted), then default groups, then unsynced custom field categories
    const result: Array<{
      type: 'custom' | 'default' | 'custom_field_category';
      key: string;
      name: string;
      icon: string;
      sortOrder: number;
      fields: UnifiedField[];
      category?: FieldCategory;
      isCustomFieldCategory?: boolean;
    }> = [];

    // Add custom categories (from FieldCategory table) - merge core fields AND matching custom fields
    categories
      .filter(cat => cat.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
      .forEach(cat => {
        const coreFieldsInCategory = customCategoryFields.get(cat.id) || [];
        const customFieldsInCategory = customFieldsInCategories.get(cat.id) || [];
        const allFields = [...coreFieldsInCategory, ...customFieldsInCategory];

        result.push({
          type: 'custom',
          key: `custom_${cat.id}`,
          name: cat.display_name,
          icon: cat.icon || '📁',
          sortOrder: cat.sort_order,
          fields: allFields.sort((a, b) => a.sort_order - b.sort_order),
          category: cat,
        });
      });

    // Add default groups for core fields
    defaultGroups.forEach((group, index) => {
      const fields = defaultGroupFields.get(group) || [];
      if (fields.length > 0) {
        result.push({
          type: 'default',
          key: `default_${group}`,
          name: group === 'custom' ? 'Custom Fields' : group.charAt(0).toUpperCase() + group.slice(1).replace('_', ' '),
          icon: FIELD_GROUP_ICONS[group] || '📋',
          sortOrder: 100 + index,
          fields: fields.sort((a, b) => a.sort_order - b.sort_order),
        });
      }
    });

    // Add unsynced custom field categories (categories not yet in FieldCategory table)
    const sortedCustomFieldCategories = Array.from(customFieldsByCategory.keys()).sort();
    sortedCustomFieldCategories.forEach((categoryName, index) => {
      const fields = customFieldsByCategory.get(categoryName) || [];
      result.push({
        type: 'custom_field_category',
        key: `cf_category_${categoryName.replace(/\s+/g, '_').toLowerCase()}`,
        name: categoryName,
        icon: '📋',
        sortOrder: 200 + index,
        fields: fields.sort((a, b) => a.sort_order - b.sort_order),
        isCustomFieldCategory: true,
      });
    });

    // Add field groups as their own section type. Children are surfaced as
    // UnifiedField entries so the existing card UI can show them, but the
    // section is read-only here — admins manage membership in the Field Groups tab.
    (fieldGroups || []).forEach((group, index) => {
      const groupFields: UnifiedField[] = group.children.map((child) => ({
        id: child.field_id,
        field_key: `group_${group.id}_${child.name}`,
        display_name: child.friendly_name,
        field_type: child.type,
        field_group: group.name,
        is_system_field: false,
        sort_order: child.group_order ?? 0,
        is_custom: true,
        category: group.friendly_name,
      } as any));
      result.push({
        type: 'field_group',
        key: `field_group_${group.id}`,
        name: group.friendly_name,
        icon: '📚',
        sortOrder: 300 + index,
        fields: groupFields,
        fieldGroupId: group.id,
        fieldGroup: group,
      } as any);
    });

    return result;
  }, [coreFields, customFields, categories, configurations, fieldGroups]);

  // Get the currently dragged field (must be after unifiedCategoryView)
  const activeField = useMemo(() => {
    if (!activeId) return null;
    const [, fieldKey] = String(activeId).split(':');
    for (const group of unifiedCategoryView) {
      const field = group.fields.find(f => f.field_key === fieldKey);
      if (field) return field;
    }
    return null;
  }, [activeId, unifiedCategoryView]);

  // Load data
  // Internal loader — silent=true skips the loading spinner so scroll position
  // is preserved when refreshing after a user action (e.g., toggling visibility)
  const loadMasterFields = useCallback(async () => {
    setMasterLoading(true);
    try {
      const rows = await membersAPI.listCustomFields({
        include_hidden: true,
        include_group_children: true,
      });
      setMasterFields(rows);
    } catch (e) {
      console.warn('All Fields: failed to load', e);
      setMasterFields([]);
    } finally {
      setMasterLoading(false);
    }
  }, [activeOrgId]);

  // All Fields loads its own org-wide list (the rest of the screen is scoped to
  // one record type). Load it when the tab is opened — the trigger used to carry
  // an onClick, but TabsTrigger accepts no such prop, so the tab opened empty and
  // only filled in once the user found the Refresh button.
  useEffect(() => {
    if (activeConfigTab === 'all-fields') {
      loadMasterFields();
    }
  }, [activeConfigTab, loadMasterFields]);

  const loadDataInternal = useCallback(async (silent: boolean = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [coreFieldsData, customFieldsData, categoriesData, configurationsData, settingsData, layoutData] = await Promise.all([
        membersAPI.listCoreFields({ record_type: recordScope }),
        // include_group_children: this screen configures repeatable-group children
        // (it renders fieldGroups alongside the flat fields), so it needs the rows the
        // entry forms are now correctly denied by default. TRUS-302.
        membersAPI.listCustomFields({
          include_hidden: true,
          record_type: recordScope,
          include_group_children: true,
        }),
        // Active categories only — deleting a category soft-deletes it
        // (is_active=false); fetching with include_inactive=true made the
        // deleted category reappear in the Categories tab.
        membersAPI.listMemberFieldCategories(false),
        membersAPI.listFieldConfigurations({ include_hidden: true, record_type: recordScope }),
        membersAPI.getUnifiedViewSettings(),
        // Org-level (no member_id): the tree as every member sees it, before
        // any one member's program overrides.
        membersAPI.getProfileLayout(),
      ]);

      setCoreFields(coreFieldsData);
      setCustomFields(customFieldsData);
      setCategories(categoriesData);
      setConfigurations(configurationsData);
      setUnifiedSettings(settingsData);
      setProfileLayout(layoutData);
    } catch (err: any) {
      console.error('Failed to load field configuration data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
    // activeOrgId: org switching is client-side with no reload, so without this
    // the builder kept rendering the PREVIOUS org's fields while every save went
    // to the newly selected org — which is how config rows ended up pointing at
    // another org's com_fields. Re-fetch whenever the active org changes.
  }, [recordScope, activeOrgId]);

  const loadData = useCallback(() => loadDataInternal(false), [loadDataInternal]);
  const loadDataSilent = useCallback(() => loadDataInternal(true), [loadDataInternal]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // TRUS-100 part 3: load the live profile layout for the current record type
  // whenever Preview is on (or the admin switches Members/Contacts). Activity
  // events don't have a member-profile view, so preview is members/contacts only.
  useEffect(() => {
    if (!builderPreview) return;
    if (recordScope === 'activity_event') {
      setPreviewFields([]);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    membersAPI
      .getProfileFields({ record_type: recordScope })
      .then((fields) => {
        if (!cancelled) setPreviewFields(fields);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [builderPreview, recordScope]);

  // Group the preview fields by category, ordered the way the member profile
  // renders them (category_sort, then field_sort). Mirrors
  // MemberProfileConfigured's grouping.
  const previewCategories = useMemo(() => {
    const byCat = new Map<string, { key: string; name: string; sort: number; fields: ProfileField[] }>();
    previewFields
      .filter((f) => !f.is_hidden)
      .forEach((f) => {
        const existing = byCat.get(f.category_key);
        if (existing) {
          existing.fields.push(f);
        } else {
          byCat.set(f.category_key, {
            key: f.category_key,
            name: f.category_name,
            sort: f.category_sort ?? 0,
            fields: [f],
          });
        }
      });
    return Array.from(byCat.values())
      .map((c) => ({ ...c, fields: c.fields.sort((a, b) => (a.field_sort ?? 0) - (b.field_sort ?? 0)) }))
      .sort((a, b) => a.sort - b.sort);
  }, [previewFields]);

  // Get configuration for a field (core or custom)
  const getFieldConfig = (fieldKey: string, customFieldId?: number): FieldConfiguration | undefined => {
    return configurations.find((c) => {
      // For custom fields, check custom_field_id
      if (customFieldId && c.custom_field_id === customFieldId) {
        return true;
      }
      // For core fields, check field_key or core_field_id
      if (c.field_key === fieldKey) {
        return true;
      }
      if (c.core_field_id && coreFields.find((cf) => cf.id === c.core_field_id)?.field_key === fieldKey) {
        return true;
      }
      return false;
    });
  };

  const builderFilterActive = builderSearch.trim() !== '' || builderFilter !== 'all';

  // Does a field match the Profile Builder search + status facet?
  const fieldMatchesBuilderFilter = (field: UnifiedField): boolean => {
    const q = builderSearch.trim().toLowerCase();
    if (q && !(
      (field.display_name || '').toLowerCase().includes(q) ||
      (field.field_key || '').toLowerCase().includes(q)
    )) {
      return false;
    }
    if (builderFilter === 'all') return true;
    const cfg = getFieldConfig(field.field_key, field.is_custom ? field.id : undefined);
    const isHidden = field.is_custom ? field.hidden : (cfg?.is_hidden || false);
    if (builderFilter === 'hidden') return !!isHidden;
    if (builderFilter === 'visible') return !isHidden;
    if (builderFilter === 'in_list') return !!cfg?.show_in_list;
    if (builderFilter === 'configured') return !!cfg;
    return true;
  };

  // TRUS-394: standard (core) fields flattened with their per-org configuration
  // applied, for the Field Builder tab to render alongside custom fields. Doing
  // the fold here keeps FieldConfigManager ignorant of core-field plumbing.
  const standardFieldRows: StandardFieldRow[] = coreFields.map((cf) => {
    const cfg = getFieldConfig(cf.field_key);
    return {
      field_key: cf.field_key,
      display_name: cfg?.display_name || cf.default_display_name,
      default_display_name: cf.default_display_name,
      field_type: cf.field_type,
      category: cfg?.category_name || cf.field_group || null,
      is_required: cfg?.is_required ?? false,
      is_hidden: cfg?.is_hidden ?? false,
      is_system_field: cf.is_system_field,
    };
  });

  // Categories the bulk assign can target. The bulk endpoint resolves by
  // `category_key` and silently no-ops on a key it can't find, so only the
  // structured, active FieldCategory rows qualify — the free-text categories
  // living on com_fields.category have no key to assign to.
  const bulkAssignableCategories = categories
    .filter((c) => c.is_active)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((c) => ({ category_key: c.category_key, display_name: c.display_name }));

  // Custom-field id → configured category name, where a FieldConfiguration
  // overrides the field's native com_fields.category. Profile Builder groups by
  // the override, so the Field Builder table has to show it too — otherwise a
  // bulk assign appears to do nothing (same trap as TRUS-365's display name).
  const customCategoryOverrides: Record<number, string | undefined> = {};
  customFields.forEach((f) => {
    // `custom_${name}` is the key form customToUnified uses; the bare name could
    // collide with a core field_key on getFieldConfig's fallback branch.
    const cfg = getFieldConfig(`custom_${f.name}`, f.id);
    if (cfg?.category_id) {
      customCategoryOverrides[f.id] =
        cfg.category_name ?? categories.find((c) => c.id === cfg.category_id)?.display_name;
    }
  });

  // Handle category save
  const handleSaveCategory = async () => {
    setSaving(true);
    try {
      if (editingCategory) {
        await membersAPI.updateFieldCategory(editingCategory.id, {
          display_name: categoryForm.display_name,
          description: categoryForm.description,
          icon: categoryForm.icon,
          sort_order: categoryForm.sort_order,
          is_collapsed_by_default: categoryForm.is_collapsed_by_default,
        });
      } else {
        await membersAPI.createFieldCategory(categoryForm);
      }
      await loadDataSilent();
      setShowCategoryDialog(false);
      resetCategoryForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  // Handle field configuration save
  const handleSaveFieldConfig = async () => {
    setSaving(true);
    try {
      // Send field_options for fields that carry an editable option list:
      // enum core fields (gender/race/ethnicity/preferred_language) and
      // select/radio-type fields (custom select/radio). For other field types
      // we leave it undefined to avoid clobbering the column with a noisy
      // empty array. (TRUS-100 part 4.)
      const optionsPayload = fieldSupportsOptions(selectedCoreField?.field_type)
        ? configForm.field_options ?? null
        : undefined;

      // TRUS-100 part 1: only send scope when the admin set a non-empty
      // selection; an empty array means "inherit", which we represent as null
      // so the backend falls back to the underlying field's default scope.
      const scopePayload: RecordTypeScope[] | null =
        configForm.applies_to_record_types && configForm.applies_to_record_types.length > 0
          ? configForm.applies_to_record_types
          : null;

      if (editingConfig) {
        await membersAPI.updateFieldConfiguration(editingConfig.id, {
          category_id: configForm.category_id,
          display_name: configForm.display_name || undefined,
          placeholder_text: configForm.placeholder_text || undefined,
          help_text: configForm.help_text || undefined,
          is_hidden: configForm.is_hidden,
          is_required: configForm.is_required,
          is_readonly: configForm.is_readonly,
          sort_order: configForm.sort_order,
          display_width: configForm.display_width,
          applies_to_record_types: scopePayload,
          ...(optionsPayload !== undefined ? { field_options: optionsPayload } : {}),
        });
      } else if (editingCustomFieldId) {
        // Custom field: configure via custom_field_id (not core_field_key).
        await membersAPI.createFieldConfiguration({
          custom_field_id: editingCustomFieldId,
          category_id: configForm.category_id,
          display_name: configForm.display_name || undefined,
          placeholder_text: configForm.placeholder_text || undefined,
          help_text: configForm.help_text || undefined,
          is_hidden: configForm.is_hidden,
          is_required: configForm.is_required,
          is_readonly: configForm.is_readonly,
          sort_order: configForm.sort_order,
          display_width: configForm.display_width,
          applies_to_record_types: scopePayload,
          ...(optionsPayload !== undefined ? { field_options: optionsPayload } : {}),
        });
      } else {
        await membersAPI.createFieldConfiguration({
          core_field_key: selectedCoreField?.field_key,
          ...configForm,
          applies_to_record_types: scopePayload,
          ...(optionsPayload !== undefined ? { field_options: optionsPayload } : {}),
        });
      }
      await loadDataSilent();
      setShowFieldConfigDialog(false);
      resetConfigForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle unified settings update
  const handleUpdateUnifiedSettings = async (key: keyof UnifiedViewSettings, value: boolean) => {
    if (!unifiedSettings) return;
    try {
      const updated = await membersAPI.updateUnifiedViewSettings({ [key]: value });
      setUnifiedSettings(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update settings');
    }
  };

  // Per-org member-profile terminology and ordering (TRUS-528). Bulk, because
  // a single move renumbers a whole sibling group.
  const handleSaveProfileLayout = async (overrides: ProfileLayoutOverrideInput[]) => {
    setLayoutSaving(true);
    try {
      await membersAPI.updateProfileLayout(overrides);
      // Re-read rather than patching local state: the server resolves the
      // labels (including translation) and the effective ordering, so its
      // answer is the only trustworthy one.
      setProfileLayout(await membersAPI.getProfileLayout());
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save the profile layout');
    } finally {
      setLayoutSaving(false);
    }
  };

  const handleResetProfileLayout = async () => {
    if (!confirm('Return every member-profile section and tab to its standard name and order?')) return;
    setLayoutSaving(true);
    try {
      await membersAPI.resetProfileLayout();
      setProfileLayout(await membersAPI.getProfileLayout());
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to reset the profile layout');
    } finally {
      setLayoutSaving(false);
    }
  };

  // Lazy-load the org's active programs the first time the Program overrides
  // sub-mode or Profile Layout (its Pinned forms card) is opened. Loaded once:
  // an org with no active programs must not refetch on every render.
  const programsRequested = useRef(false);
  const loadPrograms = useCallback(async () => {
    programsRequested.current = true;
    setProgramsLoading(true);
    try {
      const list = await programsAPI.listPrograms({ is_active: true });
      setPrograms(list);
    } catch (err: any) {
      programsRequested.current = false;
      setError(err.response?.data?.detail || err.message || 'Failed to load programs');
    } finally {
      setProgramsLoading(false);
    }
  }, []);

  useEffect(() => {
    const needsPrograms = visibilityMode === 'program' || activeConfigTab === 'profile-layout';
    if (needsPrograms && !programsRequested.current) {
      void loadPrograms();
    }
  }, [visibilityMode, activeConfigTab, loadPrograms]);

  // Fetch a program's explicit overrides when one is selected.
  useEffect(() => {
    if (selectedProgramId == null) {
      setProgramOverrides({});
      return;
    }
    let cancelled = false;
    setOverridesLoading(true);
    membersAPI
      .getProgramTabVisibility(selectedProgramId)
      .then((res) => {
        if (!cancelled) setProgramOverrides(res.overrides || {});
      })
      .catch((err: any) => {
        if (!cancelled) {
          setProgramOverrides({});
          setError(err.response?.data?.detail || err.message || 'Failed to load program overrides');
        }
      })
      .finally(() => {
        if (!cancelled) setOverridesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProgramId]);

  // Set a program tab override. state 'inherit' clears it (null); 'show'=true;
  // 'hide'=false.
  const handleUpdateProgramOverride = async (tabKey: string, state: OverrideState) => {
    if (selectedProgramId == null) return;
    const isVisible = state === 'inherit' ? null : state === 'show';
    setSavingOverrideKey(tabKey);
    // Optimistic local update.
    setProgramOverrides((prev) => {
      const next = { ...prev };
      if (isVisible === null) delete next[tabKey];
      else next[tabKey] = isVisible;
      return next;
    });
    try {
      await membersAPI.updateProgramTabVisibility(selectedProgramId, tabKey, isVisible);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update program override');
      // Reload authoritative state on failure.
      try {
        const res = await membersAPI.getProgramTabVisibility(selectedProgramId);
        setProgramOverrides(res.overrides || {});
      } catch {
        /* leave optimistic state */
      }
    } finally {
      setSavingOverrideKey(null);
    }
  };

  // Quick toggle field visibility (works for both CoreField and UnifiedField)
  const handleToggleFieldVisibility = async (field: CoreField | UnifiedField) => {
    // Custom fields cannot be toggled here - they need to be edited via Field Configuration page
    if ('is_custom' in field && field.is_custom) {
      return;
    }
    const existingConfig = getFieldConfig(field.field_key);
    const newHidden = existingConfig ? !existingConfig.is_hidden : true;

    // Optimistic update — update local state immediately so UI reflects change
    // without re-rendering the entire list (which would lose scroll position)
    if (existingConfig) {
      setConfigurations((prev) =>
        prev.map((c) =>
          c.id === existingConfig.id ? { ...c, is_hidden: newHidden } : c
        )
      );
    } else {
      // Add a placeholder optimistic config; will be replaced with real data after API
      setConfigurations((prev) => [
        ...prev,
        {
          id: -1, // temporary
          cbo_id: 0,
          core_field_id: ('id' in field ? field.id : 0) as number,
          field_key: field.field_key,
          is_hidden: newHidden,
          is_required: false,
          is_readonly: false,
          sort_order: 0,
          display_width: 'full',
          created_at: new Date().toISOString(),
        } as any,
      ]);
    }

    try {
      let savedConfig;
      if (existingConfig) {
        savedConfig = await membersAPI.updateFieldConfiguration(existingConfig.id, {
          is_hidden: newHidden,
        });
        // Replace optimistic with real
        setConfigurations((prev) =>
          prev.map((c) => (c.id === existingConfig.id ? savedConfig : c))
        );
      } else {
        savedConfig = await membersAPI.createFieldConfiguration({
          core_field_key: field.field_key,
          is_hidden: true,
        });
        // Replace temporary placeholder with real config from server
        setConfigurations((prev) => [
          ...prev.filter((c) => c.id !== -1 || c.field_key !== field.field_key),
          savedConfig,
        ]);
      }
    } catch (err: any) {
      // Revert optimistic update on failure
      if (existingConfig) {
        setConfigurations((prev) =>
          prev.map((c) =>
            c.id === existingConfig.id ? { ...c, is_hidden: existingConfig.is_hidden } : c
          )
        );
      } else {
        setConfigurations((prev) => prev.filter((c) => c.id !== -1));
      }
      setError(err.response?.data?.detail || err.message || 'Failed to toggle visibility');
    }
  };

  // Toggle whether a field appears as a column in the member LIST view. Works
  // for both core and custom fields (e.g. CIN/MCP are custom fields).
  const handleToggleShowInList = async (field: UnifiedField) => {
    const customId = field.is_custom ? field.id : undefined;
    const existingConfig = getFieldConfig(field.field_key, customId);
    const newShow = existingConfig ? !existingConfig.show_in_list : true;

    if (existingConfig) {
      setConfigurations((prev) =>
        prev.map((c) => (c.id === existingConfig.id ? { ...c, show_in_list: newShow } : c))
      );
    }
    try {
      let saved;
      if (existingConfig) {
        saved = await membersAPI.updateFieldConfiguration(existingConfig.id, { show_in_list: newShow });
        setConfigurations((prev) => prev.map((c) => (c.id === existingConfig.id ? saved : c)));
      } else if (field.is_custom) {
        saved = await membersAPI.createFieldConfiguration({ custom_field_id: field.id, show_in_list: true });
        setConfigurations((prev) => [...prev, saved]);
      } else {
        saved = await membersAPI.createFieldConfiguration({ core_field_key: field.field_key, show_in_list: true });
        setConfigurations((prev) => [...prev, saved]);
      }
    } catch (err: any) {
      if (existingConfig) {
        setConfigurations((prev) =>
          prev.map((c) => (c.id === existingConfig.id ? { ...c, show_in_list: existingConfig.show_in_list } : c))
        );
      }
      setError(err.response?.data?.detail || err.message || 'Failed to toggle list column');
    }
  };

  // Generic flag-toggle helper for the unified list/filter/chip framework.
  // Drives show_in_list, show_in_advanced_filter, show_as_quick_filter
  // toggles via a single code path so adding a future flag is a one-liner.
  const handleToggleFieldFlag = async (
    field: UnifiedField,
    flag: 'show_in_advanced_filter' | 'show_as_quick_filter',
  ) => {
    const customId = field.is_custom ? field.id : undefined;
    const existingConfig = getFieldConfig(field.field_key, customId);
    const newVal = existingConfig ? !(existingConfig as any)[flag] : true;

    if (existingConfig) {
      setConfigurations((prev) =>
        prev.map((c) => (c.id === existingConfig.id ? { ...c, [flag]: newVal } : c))
      );
    }
    try {
      let saved;
      if (existingConfig) {
        saved = await membersAPI.updateFieldConfiguration(existingConfig.id, { [flag]: newVal } as any);
        setConfigurations((prev) => prev.map((c) => (c.id === existingConfig.id ? saved : c)));
      } else if (field.is_custom) {
        saved = await membersAPI.createFieldConfiguration({ custom_field_id: field.id, [flag]: true } as any);
        setConfigurations((prev) => [...prev, saved]);
      } else {
        saved = await membersAPI.createFieldConfiguration({ core_field_key: field.field_key, [flag]: true } as any);
        setConfigurations((prev) => [...prev, saved]);
      }
    } catch (err: any) {
      if (existingConfig) {
        setConfigurations((prev) =>
          prev.map((c) => (c.id === existingConfig.id ? { ...c, [flag]: (existingConfig as any)[flag] } : c))
        );
      }
      setError(err.response?.data?.detail || err.message || `Failed to toggle ${flag}`);
    }
  };

  // TRUS-100 part 1: toggle a record type in the config form's
  // applies_to_record_types. We keep the form value as the explicit selection;
  // an empty array is normalized to null ("inherit/both") on save.
  const toggleConfigScope = (rt: RecordTypeScope) => {
    setConfigForm((prev) => {
      // Treat null/undefined (inherit) as "both" so unchecking from the default
      // state behaves intuitively.
      const current: RecordTypeScope[] =
        prev.applies_to_record_types && prev.applies_to_record_types.length > 0
          ? prev.applies_to_record_types
          : ['member', 'contact'];
      const next = current.includes(rt)
        ? current.filter((x) => x !== rt)
        : [...current, rt];
      return { ...prev, applies_to_record_types: next };
    });
  };

  // The set the dialog's "Applies to" checkboxes render from. null/empty =
  // inherit, which we surface as both checked.
  const effectiveScope: RecordTypeScope[] =
    configForm.applies_to_record_types && configForm.applies_to_record_types.length > 0
      ? configForm.applies_to_record_types
      : ['member', 'contact'];

  // TRUS-100 part 4: load the effective option list for an enum core field
  // from the server (per-org override or system default) into the editor.
  const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
  const loadEffectiveFieldOptions = async (fieldKey: string) => {
    setLoadingFieldOptions(true);
    try {
      const opts = await membersAPI.getFieldEffectiveOptions(fieldKey);
      setConfigForm((prev) => ({ ...prev, field_options: Array.isArray(opts) ? opts : [] }));
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load allowed values');
    } finally {
      setLoadingFieldOptions(false);
    }
  };

  // Move an allowed-value option up/down in the list (reorder).
  const moveFieldOption = (idx: number, dir: 'up' | 'down') => {
    setConfigForm((prev) => {
      const opts = [...(prev.field_options || [])];
      const target = dir === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= opts.length) return prev;
      [opts[idx], opts[target]] = [opts[target], opts[idx]];
      return { ...prev, field_options: opts };
    });
  };

  // Reset forms
  const resetCategoryForm = () => {
    setCategoryForm({
      category_key: '',
      display_name: '',
      description: '',
      icon: '',
      sort_order: 0,
      is_collapsed_by_default: false,
    });
    setEditingCategory(null);
  };

  const resetConfigForm = () => {
    setConfigForm({
      core_field_key: '',
      display_name: '',
      placeholder_text: '',
      help_text: '',
      is_hidden: false,
      is_required: false,
      is_readonly: false,
      sort_order: 0,
      display_width: 'full',
      field_options: null,
      applies_to_record_types: null,
    });
    setEditingConfig(null);
    setSelectedCoreField(null);
    setEditingCustomFieldId(null);
  };

  // Open edit dialogs
  const openEditCategory = (category: FieldCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      category_key: category.category_key,
      display_name: category.display_name,
      description: category.description || '',
      icon: category.icon || '',
      sort_order: category.sort_order,
      is_collapsed_by_default: category.is_collapsed_by_default,
    });
    setShowCategoryDialog(true);
  };

  const openEditFieldConfig = (field: CoreField | UnifiedField) => {
    const isCustom = 'is_custom' in field && !!field.is_custom;
    const customId = isCustom ? (field as UnifiedField).id : undefined;
    setEditingCustomFieldId(customId ?? null);
    const existingConfig = getFieldConfig(field.field_key, customId);
    // Resolve back to the real CoreField row so we have access to
    // default_field_options (needed to pre-seed the allowed-values editor for
    // enum fields). UnifiedField doesn't carry this field forward.
    const sourceCoreField = coreFields.find((cf) => cf.field_key === field.field_key);
    // For custom fields (select/multi_select/etc.) look up the raw CustomField
    // definition so we can pre-seed the allowed-values editor from its own
    // field_options when no FieldConfiguration row exists yet (TRUS-204).
    const sourceCustomField = isCustom
      ? customFields.find((cf) => cf.id === customId)
      : undefined;
    const coreField: CoreField = 'default_display_name' in field
      ? (field as CoreField)
      : {
          id: field.id,
          field_key: field.field_key,
          default_display_name: field.display_name,
          field_type: field.field_type,
          field_group: field.field_group,
          is_system_field: field.is_system_field,
          sort_order: field.sort_order,
          default_field_options: sourceCoreField?.default_field_options ?? null,
        };
    setSelectedCoreField(coreField);
    if (existingConfig) {
      setEditingConfig(existingConfig);
      setConfigForm({
        core_field_key: field.field_key,
        category_id: existingConfig.category_id,
        display_name: existingConfig.display_name || '',
        placeholder_text: existingConfig.placeholder_text || '',
        help_text: existingConfig.help_text || '',
        is_hidden: existingConfig.is_hidden,
        is_required: existingConfig.is_required,
        is_readonly: existingConfig.is_readonly,
        sort_order: existingConfig.sort_order,
        display_width: existingConfig.display_width,
        // Config override wins; fall back to the core-field system default
        // (enum fields like gender/race) or the custom field's own options
        // (TRUS-204: select/multi_select custom fields with no saved override).
        field_options: normalizeFieldOptions(
          existingConfig.field_options ??
            coreField.default_field_options ??
            sourceCustomField?.field_options ??
            null,
        ),
        // TRUS-100 part 1: per-cbo record-type scope (null = inherit/both).
        applies_to_record_types: existingConfig.applies_to_record_types ?? null,
      });
    } else {
      setEditingConfig(null);
      setConfigForm({
        core_field_key: field.field_key,
        display_name: '',
        placeholder_text: '',
        help_text: '',
        is_hidden: false,
        is_required: false,
        is_readonly: false,
        sort_order: field.sort_order,
        display_width: 'full',
        // For core enum fields use the system defaults; for custom select/
        // multi_select fields use the options defined on the custom field
        // itself (TRUS-204: was always null for custom fields).
        field_options: normalizeFieldOptions(
          coreField.default_field_options ??
            sourceCustomField?.field_options ??
            null,
        ),
        applies_to_record_types: null,
      });
    }
    setShowFieldConfigDialog(true);
  };

  // Toggle builder category expansion
  const toggleBuilderCategory = (key: string) => {
    setExpandedBuilderCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id || null);
  };

  // Helper function to create or update field configuration
  const createOrUpdateFieldConfig = async (
    field: UnifiedField,
    updates: { category_id?: number; sort_order?: number }
  ) => {
    const existingConfig = getFieldConfig(field.field_key, field.is_custom ? field.id : undefined);
    if (existingConfig) {
      await membersAPI.updateFieldConfiguration(existingConfig.id, updates);
    } else {
      // Create new configuration - use custom_field_id for custom fields, core_field_key for core fields
      if (field.is_custom) {
        await membersAPI.createFieldConfiguration({
          custom_field_id: field.id,
          category_id: updates.category_id,
          sort_order: updates.sort_order,
        });
      } else {
        await membersAPI.createFieldConfiguration({
          core_field_key: field.field_key,
          category_id: updates.category_id,
          sort_order: updates.sort_order,
        });
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    // Prevent concurrent drag operations
    if (bulkSaving || saving) {
      console.log('🚫 handleDragEnd blocked - operation in progress');
      return;
    }
    if (!over || !active) return;

    console.log('🎯 handleDragEnd:', { activeId: active.id, overId: over.id });

    // Parse the active (dragged) item ID: "categoryKey:fieldKey"
    const [sourceCategoryKey, sourceFieldKey] = String(active.id).split(':');

    // Find the source category and field
    const sourceCategory = unifiedCategoryView.find(c => c.key === sourceCategoryKey);
    if (!sourceCategory) return;

    const draggedField = sourceCategory.fields.find(f => f.field_key === sourceFieldKey);
    if (!draggedField) return;

    // Determine destination
    const overId = String(over.id);

    // Check if dropped on a category header (not a field)
    const targetCategory = unifiedCategoryView.find(c => c.key === overId);

    if (targetCategory) {
      // Dropped on a category - move to end of that category
      if (targetCategory.key !== sourceCategoryKey) {
        setBulkSaving(true);
        try {
          // Calculate new sort order (after all fields in target category)
          const maxSortOrder = Math.max(
            0,
            ...targetCategory.fields.map(f => f.config?.sort_order ?? f.sort_order)
          );
          const newSortOrder = maxSortOrder + 10;

          // Get the category_id for the target
          let targetCategoryId: number | undefined;
          if (targetCategory.type === 'custom' && targetCategory.category) {
            targetCategoryId = targetCategory.category.id;
          }
          // For default groups, we clear the category_id

          await createOrUpdateFieldConfig(draggedField, {
            category_id: targetCategoryId,
            sort_order: newSortOrder,
          });

          await loadDataSilent();
        } catch (err: any) {
          setError(err.response?.data?.detail || err.message || 'Failed to move field');
        } finally {
          setBulkSaving(false);
        }
      }
      return;
    }

    // Dropped on another field - parse target field ID
    const [targetCategoryKey, targetFieldKey] = overId.split(':');
    if (!targetFieldKey) return;

    const destCategory = unifiedCategoryView.find(c => c.key === targetCategoryKey);
    if (!destCategory) return;

    const targetField = destCategory.fields.find(f => f.field_key === targetFieldKey);
    if (!targetField) return;

    setBulkSaving(true);
    try {
      const sameCategory = sourceCategoryKey === targetCategoryKey;

      if (sameCategory) {
        // Reordering within the same category - include ALL fields (both core and custom)
        const categoryFields = [...destCategory.fields];
        const oldIndex = categoryFields.findIndex(f => f.field_key === sourceFieldKey);
        const newIndex = categoryFields.findIndex(f => f.field_key === targetFieldKey);

        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          setBulkSaving(false);
          return;
        }

        // Recalculate sort orders for all fields in the category
        const reorderedFields = [...categoryFields];
        const [removed] = reorderedFields.splice(oldIndex, 1);
        reorderedFields.splice(newIndex, 0, removed);

        // Update sort orders for all fields
        for (let i = 0; i < reorderedFields.length; i++) {
          const field = reorderedFields[i];
          const newSortOrder = (i + 1) * 10;
          const existingConfig = getFieldConfig(field.field_key, field.is_custom ? field.id : undefined);

          // Only update if sort order actually changed
          const currentSort = existingConfig?.sort_order ?? field.sort_order;
          if (currentSort !== newSortOrder) {
            await createOrUpdateFieldConfig(field, { sort_order: newSortOrder });
          }
        }
      } else {
        // Moving to a different category
        // Get the target category_id
        let targetCategoryId: number | undefined;
        if (destCategory.type === 'custom' && destCategory.category) {
          targetCategoryId = destCategory.category.id;
        }

        // Calculate sort order based on target field position
        const targetIndex = destCategory.fields.findIndex(f => f.field_key === targetFieldKey);
        const targetSort = targetField.config?.sort_order ?? targetField.sort_order;
        const newSortOrder = targetSort; // Place at the same position

        // First, update the dragged field's category and sort order
        await createOrUpdateFieldConfig(draggedField, {
          category_id: targetCategoryId,
          sort_order: newSortOrder,
        });

        // Shift other fields in destination category to make room
        const destFields = destCategory.fields.filter(f => f.field_key !== sourceFieldKey);
        for (let i = targetIndex; i < destFields.length; i++) {
          const field = destFields[i];
          const fieldConfig = getFieldConfig(field.field_key, field.is_custom ? field.id : undefined);
          const currentSort = fieldConfig?.sort_order ?? field.sort_order;
          if (currentSort >= newSortOrder) {
            await createOrUpdateFieldConfig(field, { sort_order: currentSort + 10 });
          }
        }
      }

      // Silent refetch — does NOT flip `loading`, so the builder stays mounted
      // and the admin's scroll position is preserved. Using loadData() here
      // swapped the whole tab to a spinner after every drop (flash + scroll to
      // top), making bulk reordering unusable.
      await loadDataSilent();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to reorder field');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  // Assign field to a category (works for both CoreField and UnifiedField)
  const handleAssignFieldToCategory = async (field: CoreField | UnifiedField, categoryId: number | null) => {
    // Custom fields cannot be assigned to categories here
    if ('is_custom' in field && field.is_custom) {
      return;
    }
    setSaving(true);
    try {
      const existingConfig = getFieldConfig(field.field_key);

      if (existingConfig) {
        await membersAPI.updateFieldConfiguration(existingConfig.id, {
          category_id: categoryId || undefined,
        });
      } else {
        await membersAPI.createFieldConfiguration({
          core_field_key: field.field_key,
          category_id: categoryId || undefined,
        });
      }

      await loadDataSilent();
      setShowAssignFieldDialog(false);
      setSelectedFieldForAssignment(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to assign field');
    } finally {
      setSaving(false);
    }
  };

  // Move field up/down within category (only for core fields / UnifiedField with is_custom=false)
  const handleMoveField = async (field: UnifiedField, direction: 'up' | 'down', categoryFields: UnifiedField[]) => {
    // Custom fields cannot be reordered via field configuration
    if (field.is_custom) {
      return;
    }
    const currentIndex = categoryFields.findIndex(f => f.field_key === field.field_key);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categoryFields.length) return;

    const targetField = categoryFields[newIndex];
    // Can only swap with other non-custom fields
    if (targetField.is_custom) {
      return;
    }

    setBulkSaving(true);
    try {
      // Swap sort orders
      const currentSort = field.config?.sort_order ?? field.sort_order;
      const targetSort = targetField.config?.sort_order ?? targetField.sort_order;

      // Update current field
      const existingConfig = getFieldConfig(field.field_key);
      if (existingConfig) {
        await membersAPI.updateFieldConfiguration(existingConfig.id, { sort_order: targetSort });
      } else {
        await membersAPI.createFieldConfiguration({
          core_field_key: field.field_key,
          sort_order: targetSort,
        });
      }

      // Update target field
      const targetConfig = getFieldConfig(targetField.field_key);
      if (targetConfig) {
        await membersAPI.updateFieldConfiguration(targetConfig.id, { sort_order: currentSort });
      } else {
        await membersAPI.createFieldConfiguration({
          core_field_key: targetField.field_key,
          sort_order: currentSort,
        });
      }

      // Silent reload — preserves scroll position
      await loadDataSilent();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to reorder field');
    } finally {
      setBulkSaving(false);
    }
  };

  // Remove field from custom category (reset to default)
  const handleRemoveFromCategory = async (field: CoreField | UnifiedField) => {
    // Custom fields cannot use this function
    if ('is_custom' in field && field.is_custom) {
      return;
    }
    const existingConfig = getFieldConfig(field.field_key);
    if (!existingConfig) return;

    setSaving(true);
    try {
      await membersAPI.updateFieldConfiguration(existingConfig.id, buildClearCategoryPayload());
      // Silent reload — preserves scroll position
      await loadDataSilent();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove field from category');
    } finally {
      setSaving(false);
    }
  };

  // Check if a category already exists in FieldCategory table
  // Compare by both category_key AND display_name to avoid duplicates
  const categoryExists = useCallback((catName: string) => {
    const key = catName.toLowerCase().replace(/\s+/g, '_');
    return categories.some(c =>
      c.category_key === key ||
      c.display_name.toLowerCase() === catName.toLowerCase()
    );
  }, [categories]);

  // Sync imported custom field categories to the FieldCategory table
  const handleSyncCategories = async () => {
    // Get unique category names from custom fields that don't exist in FieldCategory table
    const customFieldCategories = Array.from(new Set(
      customFields
        .filter(f => f.category && f.category.trim() !== '')
        .map(f => f.category!)
    ));

    const newCategories = customFieldCategories.filter(catName => !categoryExists(catName));

    if (newCategories.length === 0) {
      setError('All custom field categories are already synced.');
      return;
    }

    if (!confirm(`This will create ${newCategories.length} new category/categories:\n\n${newCategories.join('\n')}\n\nContinue?`)) {
      return;
    }

    setSyncingCategories(true);
    setError(null);

    try {
      // Create each new category
      for (let i = 0; i < newCategories.length; i++) {
        const catName = newCategories[i];
        await membersAPI.createFieldCategory({
          category_key: catName.toLowerCase().replace(/\s+/g, '_'),
          display_name: catName,
          description: `Imported from Field Configuration - ${catName}`,
          icon: '📋',
          sort_order: 50 + i,
          is_collapsed_by_default: false,
        });
      }

      await loadDataSilent();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to sync categories');
    } finally {
      setSyncingCategories(false);
    }
  };

  // TRUS-109 — field-map round-trip. Export downloads an .xlsx of every data field
  // with the 15 setup columns (incl. per-context MIF/Assessment/Care Plan/Billing/RTF);
  // Import applies an edited file back into member_field_form_contexts.
  const [fieldMapBusy, setFieldMapBusy] = useState(false);
  const handleExportFieldMap = async () => {
    setFieldMapBusy(true);
    setError(null);
    try {
      const blob = await membersAPI.exportFieldMap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'weavein_field_map.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to export field map');
    } finally {
      setFieldMapBusy(false);
    }
  };
  const handleImportFieldMap = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setFieldMapBusy(true);
    setError(null);
    try {
      const res = await membersAPI.importFieldMap(file);
      const skipped = res.rows_skipped
        ? ` ${res.rows_skipped} unrecognized field${res.rows_skipped === 1 ? '' : 's'} skipped${res.skipped_examples?.length ? ` (e.g. ${res.skipped_examples.slice(0, 3).join(', ')})` : ''}.`
        : '';
      alert(`Field map applied: ${res.fields_updated} field${res.fields_updated === 1 ? '' : 's'} updated.${skipped}`);
      await loadDataSilent();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to import field map');
    } finally {
      setFieldMapBusy(false);
    }
  };

  // TRUS-100 part 2: one-click section presets. The Profile Builder needs the
  // Family Member / Caregiver / Household sections to exist as FieldCategory
  // rows so fields can be assigned to them. FIELD_GROUP_ICONS already carries
  // the right emoji for each.
  const SECTION_PRESETS: { category_key: string; display_name: string; icon: string }[] = [
    { category_key: 'family_member', display_name: 'Family Member', icon: FIELD_GROUP_ICONS.family_member },
    { category_key: 'caregiver', display_name: 'Caregiver', icon: FIELD_GROUP_ICONS.caregiver },
    { category_key: 'household', display_name: 'Household', icon: FIELD_GROUP_ICONS.household },
  ];

  const handleCreatePresetSection = async (
    preset: { category_key: string; display_name: string; icon: string },
  ) => {
    // Skip if a category with this key or display name already exists.
    if (categoryExists(preset.display_name) || categories.some(c => c.category_key === preset.category_key)) {
      setError(`The "${preset.display_name}" section already exists.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await membersAPI.createFieldCategory({
        category_key: preset.category_key,
        display_name: preset.display_name,
        description: `${preset.display_name} details`,
        icon: preset.icon,
        sort_order: 60 + categories.length,
        is_collapsed_by_default: false,
      });
      await loadDataSilent();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create section');
    } finally {
      setSaving(false);
    }
  };

  // A custom field is "unsynced" only if it has a category STRING that matches
  // no FieldCategory AND it isn't already assigned to a real category via
  // config.category_id. Without the category_id check, a field assigned by id
  // but carrying a stale/typo category string (e.g. "Deconflictiion Status"
  // assigned to category 317) showed up as a phantom unsynced category.
  const isFieldUnsynced = useCallback((f: CustomField): boolean => {
    if (!f.category || f.category.trim() === '') return false;
    if (categoryExists(f.category)) return false;
    if (configurations.find(c => c.custom_field_id === f.id)?.category_id) return false;
    return true;
  }, [categoryExists, configurations]);

  // Get count of unsynced categories
  const unsyncedCategoryCount = useMemo(() => {
    return new Set(customFields.filter(isFieldUnsynced).map(f => f.category!)).size;
  }, [customFields, isFieldUnsynced]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading field configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <ApiErrorAlert
          error={error}
          onRetry={loadData}
          retryable={true}
        />
      )}

      {/* Record-type scope: members and contacts share the same fields table.
          Toggle filters the manager to show fields applicable to one record type. */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Configure fields for:
        </span>
        {/* One button per assignable record type, driven by RECORD_TYPE_SCOPES so the
            picker, the scope column and the All Fields tab can't disagree about which
            record types exist or what they're called. */}
        <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          {RECORD_TYPE_SCOPES.filter((s) => s.assignable).map((scope, i) => (
            <button
              key={scope.value}
              type="button"
              onClick={() => setRecordScope(scope.value)}
              title={scope.hint}
              className={`px-3 py-1.5 text-sm font-medium ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${
                recordScope === scope.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {scope.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          Shows only fields whose scope includes this record type.
        </span>
      </div>

      <Tabs value={activeConfigTab} onValueChange={setActiveConfigTab} className="space-y-4">
        {/* Group selector, then the tabs inside the active group (TRUS-394). */}
        <div className="space-y-2">
          <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            {TAB_GROUPS.map((group, i) => (
              <button
                key={group.id}
                type="button"
                title={group.hint}
                aria-pressed={group.id === activeTabGroup.id}
                onClick={() => setActiveConfigTab(group.tabs[0].value)}
                className={`px-4 py-2 text-sm font-semibold ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${
                  group.id === activeTabGroup.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {group.label}
              </button>
            ))}
          </div>
          <TabsList>
            {activeTabGroup.tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.value === 'profile-builder' && <Layout className="h-4 w-4 mr-2" />}
                {tab.value === 'custom-fields' && <Settings2 className="h-4 w-4 mr-2" />}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Profile Layout — per-org terminology and ordering (TRUS-528) */}
        <TabsContent value="profile-layout" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-4 w-4" />
                Profile Layout
              </CardTitle>
              <CardDescription>
                Use your organization&apos;s own words for the member profile, and
                order the sections and tabs the way your staff work.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProfileLayoutEditor
                layout={profileLayout}
                saving={layoutSaving}
                onSave={handleSaveProfileLayout}
                onReset={handleResetProfileLayout}
              />
            </CardContent>
          </Card>

          {/* Pinned forms (TRUS-564; program + role scoping TRUS-762): put one
              form in the member sidebar for one program and chosen roles. The
              editor above renames, orders and places pins like any tab. Only
              for an org on the TRUS-762 member profile (member_profile_v2). */}
          {memberProfileV2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pin className="h-4 w-4" />
                {t('pinnedForms.title', 'Pinned forms')}
              </CardTitle>
              <CardDescription>
                {t(
                  'pinnedForms.description',
                  'Put a form in the member sidebar. It shows only on members enrolled in its program, only to the roles you choose, and opens that form: continue the open draft or start a new one.',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PinnedFormsManager
                programs={programs}
                orgId={activeOrgId ? Number(activeOrgId) : null}
                onChanged={() => {
                  void membersAPI.getProfileLayout().then(setProfileLayout).catch(() => undefined);
                }}
              />
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* Profile Builder Tab - Unified View */}
        <TabsContent value="profile-builder" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5" />
                {t('member_fields.profile_builder', 'Profile Builder')}
              </CardTitle>
              <CardDescription>
                {t('member_fields.profile_builder_desc', 'Organize your member profile UI. Drag fields between categories, reorder them, and preview the final layout. Custom categories appear first, followed by default field groups.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Quick Actions Bar */}
              <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge variant="outline" className="gap-1">
                    <Layers className="h-3 w-3" />
                    {unifiedCategoryView.filter(c => c.type === 'custom').length} Custom Categories
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    <FolderOpen className="h-3 w-3" />
                    {unifiedCategoryView.filter(c => c.type === 'default').length} Standard Field Groups
                  </Badge>
                  {unifiedCategoryView.filter(c => c.type === 'custom_field_category').length > 0 && (
                    <Badge variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-200">
                      <FolderOpen className="h-3 w-3" />
                      {unifiedCategoryView.filter(c => c.type === 'custom_field_category').length} Imported Categories
                    </Badge>
                  )}
                  <Badge variant="outline" className="gap-1">
                    <Settings2 className="h-3 w-3" />
                    {coreFields.length} Standard Fields
                  </Badge>
                  {customFields.length > 0 && (
                    <Badge variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-200">
                      <Settings2 className="h-3 w-3" />
                      {customFields.length} Custom Fields
                    </Badge>
                  )}
                  {fieldGroups.length > 0 && (
                    <Badge variant="outline" className="gap-1 bg-purple-50 text-purple-700 border-purple-200">
                      <Layers className="h-3 w-3" />
                      {fieldGroups.length} Field Group{fieldGroups.length === 1 ? '' : 's'}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  {/* Preview toggle — TRUS-100 part 3. Rendered unconditionally
                      (always visible whenever the Profile Builder tab is open)
                      and clearly labeled with the record type it previews, so
                      users can confirm exactly what a Member/Contact will see.
                      Flips the builder into a read-only mirror of the current
                      record type's profile layout. */}
                  <Button
                    variant={builderPreview ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBuilderPreview((v) => !v)}
                    title="Preview the layout as it appears on the member/contact profile"
                  >
                    {builderPreview ? (
                      <>
                        <Pencil className="h-4 w-4 mr-2" />
                        Back to editing
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview as {recordScope === 'contact' ? 'Contact' : recordScope === 'activity_event' ? 'Activity Event' : 'Member'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Expand all
                      setExpandedBuilderCategories(new Set(unifiedCategoryView.map(c => c.key)));
                    }}
                  >
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandedBuilderCategories(new Set())}
                  >
                    Collapse All
                  </Button>
                  {unsyncedCategoryCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSyncCategories}
                      disabled={syncingCategories}
                      className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                    >
                      {syncingCategories ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Sync {unsyncedCategoryCount} Categories
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      exportFieldConfigToCSV(
                        { coreFields, customFields, categories, configurations, fieldGroups },
                        'field_configuration',
                      )
                    }
                    disabled={coreFields.length === 0 && customFields.length === 0}
                    title="Export every core and custom field (with field-group membership, categories, and rules) to a CSV spreadsheet"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  {/* TRUS-109 — Field map (Excel round-trip): export the 15-column
                      setup sheet (incl. per-context MIF/Assessment/Care Plan/Billing/RTF),
                      edit, and re-import to apply the context mappings. */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportFieldMap}
                    disabled={fieldMapBusy}
                    title="Download every data field with the 15 setup columns (MIF, Assessment, Care Plan, Billing, RTF) as an Excel file to review and edit"
                  >
                    {fieldMapBusy ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                    Field Map (Excel)
                  </Button>
                  <input
                    id="field-map-import-input"
                    type="file"
                    accept=".xlsx,.csv"
                    className="hidden"
                    onChange={handleImportFieldMap}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('field-map-import-input')?.click()}
                    disabled={fieldMapBusy}
                    title="Upload an edited field-map file to apply the MIF/Assessment/Care Plan/Billing/RTF mappings"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import Map
                  </Button>
                  {/* Add section — TRUS-100 part 2. One-click presets for the
                      common record-type sections plus a custom-category option. */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Section
                        <ChevronDown className="h-4 w-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Quick-add a section</DropdownMenuLabel>
                      {SECTION_PRESETS.map((preset) => {
                        const exists =
                          categoryExists(preset.display_name) ||
                          categories.some((c) => c.category_key === preset.category_key);
                        return (
                          <DropdownMenuItem
                            key={preset.category_key}
                            disabled={exists || saving}
                            onSelect={(e) => {
                              e.preventDefault();
                              handleCreatePresetSection(preset);
                            }}
                          >
                            <span className="text-lg mr-2">{preset.icon}</span>
                            {preset.display_name}
                            {exists && (
                              <span className="ml-auto text-xs text-muted-foreground">Added</span>
                            )}
                          </DropdownMenuItem>
                        );
                      })}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => setShowCategoryDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Custom section…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Quick-add section presets — TRUS-100 part 2. Surfaced as
                  visible chips (not buried in the "Add Section" menu) so users
                  can see and one-click the common record-type sections. The
                  dropdown above is kept for the custom-section option. Hidden
                  in read-only Preview mode. */}
              <div className={`mb-4 flex flex-wrap items-center gap-2 ${builderPreview ? 'hidden' : ''}`}>
                <span className="text-xs font-medium text-muted-foreground mr-1">
                  Quick-add section:
                </span>
                {SECTION_PRESETS.map((preset) => {
                  const exists =
                    categoryExists(preset.display_name) ||
                    categories.some((c) => c.category_key === preset.category_key);
                  return (
                    <Button
                      key={preset.category_key}
                      variant="outline"
                      size="sm"
                      disabled={exists || saving}
                      onClick={() => handleCreatePresetSection(preset)}
                      title={exists ? `${preset.display_name} section already added` : `Add a ${preset.display_name} section`}
                      className="h-8"
                    >
                      <span className="text-base mr-1.5">{preset.icon}</span>
                      {preset.display_name}
                      {exists ? (
                        <span className="ml-2 text-xs text-muted-foreground">Added</span>
                      ) : (
                        <Plus className="h-3.5 w-3.5 ml-1.5" />
                      )}
                    </Button>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCategoryDialog(true)}
                  className="h-8 text-muted-foreground"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Custom section…
                </Button>
              </div>

              {/* Filter bar — find fields by name and by status (visible /
                  hidden / in member-list columns / configured). Hidden in
                  read-only Preview mode (TRUS-100 part 3). */}
              <div className={`flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/40 ${builderPreview ? 'hidden' : ''}`}>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={builderSearch}
                    onChange={(e) => {
                      setBuilderSearch(e.target.value);
                      if (e.target.value.trim()) setExpandedBuilderCategories(new Set(unifiedCategoryView.map(c => c.key)));
                    }}
                    placeholder="Search fields by name…"
                    className="h-9 w-56 rounded-md border border-gray-300 bg-white pl-8 pr-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  />
                </div>
                <Select
                  value={builderFilter}
                  onValueChange={(v) => {
                    setBuilderFilter(v as any);
                    if (v !== 'all') setExpandedBuilderCategories(new Set(unifiedCategoryView.map(c => c.key)));
                  }}
                >
                  <SelectTrigger className="h-9 w-52 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All fields</SelectItem>
                    <SelectItem value="in_list">In list columns</SelectItem>
                    <SelectItem value="visible">Visible on profile</SelectItem>
                    <SelectItem value="hidden">Hidden on profile</SelectItem>
                    <SelectItem value="configured">Configured (overridden)</SelectItem>
                  </SelectContent>
                </Select>
                {builderFilterActive && (
                  <button
                    type="button"
                    onClick={() => { setBuilderSearch(''); setBuilderFilter('all'); }}
                    className="text-xs text-gray-500 underline hover:text-gray-700"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Preview mode — TRUS-100 part 3. Read-only layout mirroring the
                  member/contact profile for the current record scope. */}
              {builderPreview ? (
                <div className="space-y-3">
                  <div className="rounded-md border border-dashed border-indigo-300 bg-indigo-50/50 p-3 text-sm text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                    Previewing the{' '}
                    <strong>{recordScope === 'contact' ? 'Contact' : 'Member'}</strong>{' '}
                    profile layout — sections and fields appear in the same order the profile renders them. This view is read-only.
                  </div>
                  {recordScope === 'activity_event' ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Activity Events don&apos;t have a profile-view preview. Switch to Members or Contacts.</p>
                    </div>
                  ) : previewLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                      <span className="ml-3 text-muted-foreground text-sm">Loading preview…</span>
                    </div>
                  ) : previewCategories.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Layout className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No visible fields for this record type.</p>
                    </div>
                  ) : (
                    previewCategories.map((cat) => (
                      <div key={cat.key} className="border rounded-lg overflow-hidden bg-card">
                        <div className="flex items-center gap-2 p-3 border-b bg-muted/40">
                          <span className="text-lg">{FIELD_GROUP_ICONS[cat.key] || '📋'}</span>
                          <span className="font-medium">{cat.name}</span>
                          <Badge variant="outline" className="ml-auto text-xs">
                            {cat.fields.length} field{cat.fields.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
                          {cat.fields.map((f) => (
                            <div
                              key={f.field_key}
                              className={`rounded-md border border-border p-2 ${
                                f.display_width === 'full' ? 'md:col-span-2' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{f.display_name}</span>
                                {f.is_required && (
                                  <Badge variant="destructive" className="text-xs">Required</Badge>
                                )}
                                {!f.is_core_field && (
                                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">Custom</Badge>
                                )}
                                {f.is_readonly && (
                                  <Badge variant="outline" className="text-xs">Read-only</Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                <code className="bg-muted px-1 rounded">{f.field_key}</code>
                                <span className="ml-2 capitalize">{f.field_type}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
              /* Unified Category + Fields View with Drag and Drop */
              <DndContext
                sensors={sensors}
                collisionDetection={fieldFirstCollisionDetection}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <div className="space-y-3">
                  {unifiedCategoryView.map((categoryGroup) => {
                    const isExpanded = expandedBuilderCategories.has(categoryGroup.key);
                    const hiddenCount = categoryGroup.fields.filter(f => f.is_custom ? f.hidden : f.config?.is_hidden).length;
                    const visibleCount = categoryGroup.fields.length - hiddenCount;
                    const isOverCategory = overId === categoryGroup.key;
                    // Apply the Profile Builder search/status filter. When active,
                    // categories with no matching fields are hidden entirely.
                    const displayFields = builderFilterActive
                      ? categoryGroup.fields.filter(fieldMatchesBuilderFilter)
                      : categoryGroup.fields;
                    if (builderFilterActive && displayFields.length === 0) return null;
                    const fieldIds = displayFields.map(f => `${categoryGroup.key}:${f.field_key}`);

                    return (
                      <DroppableCategory
                        key={categoryGroup.key}
                        categoryKey={categoryGroup.key}
                        isOver={isOverCategory}
                      >
                        <div
                          className={`border rounded-lg overflow-hidden ${
                            categoryGroup.type === 'custom'
                              ? 'border-primary/30 bg-primary/5'
                              : categoryGroup.type === 'custom_field_category'
                              ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                              : (categoryGroup as any).type === 'field_group'
                              ? 'border-purple-300 bg-purple-50/40 dark:border-purple-700 dark:bg-purple-900/20'
                              : 'border-border bg-card'
                          } ${isOverCategory ? 'ring-2 ring-primary ring-inset' : ''}`}
                        >
                          {/* Category Header */}
                          <div
                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                              isExpanded ? 'border-b' : ''
                            }`}
                            onClick={() => toggleBuilderCategory(categoryGroup.key)}
                          >
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-xl">{categoryGroup.icon}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{categoryGroup.name}</span>
                                {categoryGroup.type === 'custom' && (
                                  <Badge variant="secondary" className="text-xs">Custom Category</Badge>
                                )}
                                {categoryGroup.type === 'default' && (
                                  <Badge variant="outline" className="text-xs">Standard Fields</Badge>
                                )}
                                {categoryGroup.type === 'custom_field_category' && (
                                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                    Imported Category
                                  </Badge>
                                )}
                                {(categoryGroup as any).type === 'field_group' && (
                                  <>
                                    <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                      Field Group
                                    </Badge>
                                    {(categoryGroup as any).fieldGroup?.is_repeatable && (
                                      <Badge variant="outline" className="text-xs">Repeatable</Badge>
                                    )}
                                  </>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {visibleCount} visible field{visibleCount !== 1 ? 's' : ''}
                                {hiddenCount > 0 && `, ${hiddenCount} hidden`}
                                {categoryGroup.type === 'custom_field_category' && (
                                  <span className="ml-2 text-blue-600 dark:text-blue-400">• From Field Configuration</span>
                                )}
                                {(categoryGroup as any).type === 'field_group' && (
                                  <span className="ml-2 text-purple-600 dark:text-purple-400">• Manage members in the Field Groups tab</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {categoryGroup.type === 'custom' && categoryGroup.category && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditCategory(categoryGroup.category!);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {(categoryGroup as any).type === 'field_group' && (categoryGroup as any).fieldGroupId && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFocusFieldGroupId((categoryGroup as any).fieldGroupId);
                                    setActiveConfigTab('field-groups');
                                  }}
                                  title="Open Field Groups tab and edit this group's members"
                                >
                                  <Pencil className="h-3.5 w-3.5 mr-1" />
                                  Manage members
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Fields List with Sortable Context */}
                          {isExpanded && (
                            <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
                              <div className="divide-y divide-border">
                                {displayFields.length === 0 ? (
                                  <div className="p-4 text-center text-muted-foreground text-sm">
                                    <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>No fields assigned to this category</p>
                                    {(categoryGroup as any).type === 'field_group' ? (
                                      // Field-group members live in field_groups.children (NOT in
                                      // member_field_configurations like ordinary category fields),
                                      // so the drag-and-drop path here doesn't work. Route the admin
                                      // to the Field Groups tab where ChildrenEditor handles it.
                                      <>
                                        <p className="text-xs mt-1">
                                          Field-group members are managed separately.
                                        </p>
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="text-xs"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const gid = (categoryGroup as any).fieldGroupId;
                                            if (gid) setFocusFieldGroupId(gid);
                                            setActiveConfigTab('field-groups');
                                          }}
                                        >
                                          Open Field Groups tab to add members →
                                        </Button>
                                      </>
                                    ) : (
                                      <p className="text-xs mt-1">
                                        Drag fields here from other categories to add them
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  displayFields.map((field) => {
                                    const isHidden = field.is_custom ? field.hidden : (field.config?.is_hidden || false);
                                    const cfg = getFieldConfig(field.field_key, field.is_custom ? field.id : undefined) as any;
                                    const showInList = cfg?.show_in_list || false;
                                    const showInAdvancedFilter = cfg?.show_in_advanced_filter || false;
                                    const showAsQuickFilter = cfg?.show_as_quick_filter || false;

                                    return (
                                      <SortableFieldItem
                                        key={field.field_key}
                                        field={field}
                                        categoryKey={categoryGroup.key}
                                        isHidden={isHidden}
                                        onToggleVisibility={() => handleToggleFieldVisibility(field)}
                                        showInList={showInList}
                                        onToggleShowInList={() => handleToggleShowInList(field)}
                                        showInAdvancedFilter={showInAdvancedFilter}
                                        onToggleShowInAdvancedFilter={() => handleToggleFieldFlag(field, 'show_in_advanced_filter')}
                                        showAsQuickFilter={showAsQuickFilter}
                                        onToggleShowAsQuickFilter={() => handleToggleFieldFlag(field, 'show_as_quick_filter')}
                                        onEdit={() => openEditFieldConfig(field)}
                                        onRemoveFromCategory={
                                          categoryGroup.type === 'custom' && field.config?.category_id
                                            ? () => handleRemoveFromCategory(field)
                                            : undefined
                                        }
                                        onAssignToCategory={
                                          (categoryGroup.type === 'default' || categoryGroup.type === 'custom_field_category') &&
                                          categories.filter(c => c.is_active).length > 0
                                            ? () => {
                                                setSelectedFieldForAssignment(field);
                                                setShowAssignFieldDialog(true);
                                              }
                                            : undefined
                                        }
                                        canAssign={
                                          (categoryGroup.type === 'default' || categoryGroup.type === 'custom_field_category') &&
                                          categories.filter(c => c.is_active).length > 0
                                        }
                                        canRemove={categoryGroup.type === 'custom' && !!field.config?.category_id}
                                        disabled={bulkSaving || saving}
                                      />
                                    );
                                  })
                                )}
                              </div>
                            </SortableContext>
                          )}
                        </div>
                      </DroppableCategory>
                    );
                  })}

                  {/* Empty State */}
                  {unifiedCategoryView.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">
                        No {recordScope === 'activity_event' ? 'Activity Event' : recordScope === 'contact' ? 'Contact' : 'Member'} Fields Configured
                      </h3>
                      <p className="text-sm max-w-md mx-auto">
                        Nothing tagged with this record type yet.
                        {recordScope === 'member' && (
                          <>
                            {' '}If you&apos;re looking for outreach / event fields, switch to{' '}
                            <button
                              type="button"
                              onClick={() => setRecordScope('activity_event')}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              Activity Events
                            </button>
                            {' '}above.
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Drag Overlay - shows the field being dragged */}
                <DragOverlay>
                  {activeField && (
                    <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border-2 border-primary rounded-lg shadow-lg">
                      <GripVertical className="h-4 w-4 text-primary" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{activeField.display_name}</span>
                          {activeField.is_custom && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">Custom</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <code className="bg-muted px-1 rounded">{activeField.field_key}</code>
                        </div>
                      </div>
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
              )}

              {/* Legend and Help */}
              <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                <h4 className="text-sm font-medium mb-3">How to Use</h4>
                <div className="mb-4 text-sm text-muted-foreground space-y-1">
                  <p><strong>Drag & Drop:</strong> Grab the <GripVertical className="h-4 w-4 inline" /> handle to drag any field (core or custom) between categories or reorder within a category.</p>
                  <p><strong>Categories:</strong> Create custom categories (e.g., "Pregnancy & Health") to logically group fields for users.</p>
                  <p><strong>Reordering:</strong> Both core and custom fields can be reordered to match your organization's workflow and data collection needs.</p>
                </div>
                <h4 className="text-sm font-medium mb-3">Legend</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4" />
                    <span>Drag Handle</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded border-2 border-primary/30 bg-primary/5" />
                    <span>Custom Category</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded border-2 border-border bg-card" />
                    <span>Standard Field Group</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded border-2 border-blue-300 bg-blue-50" />
                    <span>Imported Category</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 h-4 px-1">CF</Badge>
                    <span>Custom Field</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4" />
                    <span>Hidden Field</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Field Builder — standard + custom field definitions in one table.
            Standard fields are owned here (coreFields + their FieldConfiguration
            rows), so they're passed down and their edits come back up. */}
        <TabsContent value="custom-fields" className="space-y-4">
          <FieldConfigManager
            onNavigate={_onNavigate}
            recordType={recordScope}
            standardFields={standardFieldRows}
            onEditStandardField={(fieldKey) => {
              const cf = coreFields.find((f) => f.field_key === fieldKey);
              if (cf) openEditFieldConfig(cf);
            }}
            onToggleStandardVisibility={(fieldKey) => {
              const cf = coreFields.find((f) => f.field_key === fieldKey);
              if (cf) handleToggleFieldVisibility(cf);
            }}
            assignableCategories={bulkAssignableCategories}
            customCategoryOverrides={customCategoryOverrides}
            onBulkApplied={() => loadData()}
          />
        </TabsContent>

        {/* Field Groups Tab — repeatable groups of custom fields */}
        <TabsContent value="field-groups" className="space-y-4">
          <FieldGroupsManager
            focusGroupId={focusFieldGroupId}
            onFocusConsumed={() => setFocusFieldGroupId(null)}
            onGroupsChanged={refetchFieldGroups}
          />
        </TabsContent>

        {/* Program Overrides Tab — per-program visible/required layered on the org base */}
        <TabsContent value="program-overrides" className="space-y-4">
          <ProgramFieldOverridesManager />
        </TabsContent>

        {/* Field Studio Tab — multi-artifact preview of a program's configured fields */}
        <TabsContent value="field-studio" className="space-y-4">
          <FieldStudio />
        </TabsContent>

        {/* Compare & Clone Tab — cross-program field matrix + clone overrides */}
        <TabsContent value="compare-clone" className="space-y-4">
          <ProgramCompareCloneManager />
        </TabsContent>

        {/* AI Assistant Tab — generate fields from a description/spreadsheet + suggest improvements */}
        <TabsContent value="ai-assistant" className="space-y-4">
          <FieldConfigAIAssistant />
        </TabsContent>

        {/* Field Mappings Tab — external column → internal field aliases used by Integration Workbench */}
        <TabsContent value="field-mappings" className="space-y-4">
          <FieldMappingManager />
        </TabsContent>

        {/* All Fields — every custom field in the org across every record type, which
            no other tab shows: the rest are scoped to whichever record type the picker
            is on. This is the view for answering "where does this field actually live"
            and for spotting the same field duplicated across two record types. */}
        <TabsContent value="all-fields" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Fields</CardTitle>
              <CardDescription>
                Every standard and custom field in this organization, across all record
                types. The Origin column marks which is which. Includes hidden fields and
                the children of repeatable groups, so nothing is missing from the picture.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Input
                  placeholder="Search name or label..."
                  value={masterQuery}
                  onChange={(e) => setMasterQuery(e.target.value)}
                  className="max-w-xs"
                />
                <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setMasterScope('all')}
                    className={`px-3 py-1.5 text-sm font-medium ${
                      masterScope === 'all'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    All
                  </button>
                  {RECORD_TYPE_SCOPES.map((sc) => (
                    <button
                      key={sc.value}
                      type="button"
                      title={sc.hint}
                      onClick={() => setMasterScope(sc.value)}
                      className={`px-3 py-1.5 text-sm font-medium border-l border-gray-200 dark:border-gray-700 ${
                        masterScope === sc.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {sc.label}
                    </button>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => loadMasterFields()}>
                  Refresh
                </Button>
              </div>

              {masterLoading ? (
                <p className="text-sm text-muted-foreground">Loading every field…</p>
              ) : (
                (() => {
                  // Merge standard (core) + custom fields into one list so admins see
                  // the whole picture in a single table, with an Origin column marking
                  // which is which (TRUS-394 item 1). Core rows fold in any per-org
                  // configuration (display name, category, hidden/required) so they read
                  // the same way custom fields do.
                  type AllFieldRow = {
                    key: string;
                    origin: 'standard' | 'custom';
                    name: string;
                    label: string;
                    type: string;
                    category: string | null;
                    appliesTo: RecordTypeScope[] | null;
                    hidden: boolean;
                    required: boolean;
                    inGroup: boolean;
                  };

                  const coreRows: AllFieldRow[] = coreFields.map((cf) => {
                    const cfg = getFieldConfig(cf.field_key);
                    return {
                      key: `core-${cf.id}`,
                      origin: 'standard',
                      name: cf.field_key,
                      label: cfg?.display_name || cf.default_display_name,
                      type: cf.field_type,
                      category: cfg?.category_name || cf.field_group || null,
                      appliesTo: cfg?.applies_to_record_types ?? null,
                      hidden: cfg?.is_hidden ?? false,
                      required: cfg?.is_required ?? false,
                      inGroup: false,
                    };
                  });

                  const customRows: AllFieldRow[] = masterFields.map((f) => ({
                    key: `custom-${f.id}`,
                    origin: 'custom',
                    name: f.name,
                    label: f.friendly_name,
                    type: f.type,
                    category: f.category || null,
                    appliesTo: f.applies_to_record_types ?? null,
                    hidden: !!f.hidden,
                    required: !!f.mandatory,
                    inGroup: !!(f as any).field_group_id,
                  }));

                  const allRows = [...coreRows, ...customRows];

                  const q = masterQuery.trim().toLowerCase();
                  const rows = allRows.filter((f) => {
                    // No explicit scope means the field inherits members + contacts.
                    const scope =
                      f.appliesTo && f.appliesTo.length > 0
                        ? f.appliesTo
                        : (['member', 'contact'] as RecordTypeScope[]);
                    const scopeOk = masterScope === 'all' || scope.includes(masterScope);
                    const qOk =
                      !q ||
                      f.name.toLowerCase().includes(q) ||
                      (f.label || '').toLowerCase().includes(q);
                    return scopeOk && qOk;
                  });
                  if (rows.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">
                        No fields match. {allRows.length === 0 && 'Try Refresh.'}
                      </p>
                    );
                  }
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-border">
                            <th className="py-2 pr-4 font-medium">Field</th>
                            <th className="py-2 pr-4 font-medium">Label</th>
                            <th className="py-2 pr-4 font-medium">Origin</th>
                            <th className="py-2 pr-4 font-medium">Type</th>
                            <th className="py-2 pr-4 font-medium">Category</th>
                            <th className="py-2 pr-4 font-medium">Applies to</th>
                            <th className="py-2 pr-4 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((f) => (
                            <tr key={f.key} className="border-b border-border/50">
                              <td className="py-2 pr-4 font-mono text-xs">{f.name}</td>
                              <td className="py-2 pr-4">{f.label}</td>
                              <td className="py-2 pr-4">
                                <Badge variant={f.origin === 'standard' ? 'secondary' : 'outline'}>
                                  {f.origin === 'standard' ? 'Standard' : 'Custom'}
                                </Badge>
                              </td>
                              <td className="py-2 pr-4 text-muted-foreground">{f.type}</td>
                              <td className="py-2 pr-4 text-muted-foreground">{f.category || '—'}</td>
                              <td className="py-2 pr-4">
                                {recordTypeScopeLabel(f.appliesTo)}
                              </td>
                              <td className="py-2 pr-4 space-x-2">
                                {f.hidden && <Badge variant="outline">Hidden</Badge>}
                                {f.required && <Badge variant="outline">Required</Badge>}
                                {f.inGroup && <Badge variant="outline">In group</Badge>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-muted-foreground mt-3">
                        {rows.length} of {allRows.length} fields
                      </p>
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5" />
                  Field Categories
                </CardTitle>
                <CardDescription>
                  Create custom categories to group fields in the member profile. For example, create separate "Mailing Address" and "Physical Address" categories.
                  Use the "Profile Builder" tab to assign fields to categories.
                </CardDescription>
              </div>
              <Button onClick={() => setShowCategoryDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category Key</TableHead>
                    <TableHead>Display Name</TableHead>
                    <TableHead>Fields</TableHead>
                    <TableHead>Sort Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Synced categories from FieldCategory table */}
                  {categories.map((category) => {
                    // Count from the SAME grouping the Profile Builder renders
                    // (unifiedCategoryView), so the count always matches what's
                    // actually shown. The previous logic counted custom fields
                    // by their category STRING while the builder groups by
                    // config.category_id (which wins) — a field reassigned via
                    // drag was then counted here but rendered under a different
                    // category, inflating the count (e.g. "1 core, 4 custom"
                    // when only 1 field actually showed).
                    const uvGroup = unifiedCategoryView.find(g => g.key === `custom_${category.id}`);
                    const groupFields = uvGroup?.fields || [];
                    const coreFieldsCount = groupFields.filter(f => !f.is_custom).length;
                    const customFieldsCount = groupFields.filter(f => f.is_custom).length;
                    const totalFieldsCount = coreFieldsCount + customFieldsCount;

                    return (
                      <TableRow key={category.id}>
                        <TableCell className="font-mono text-sm">{category.category_key}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {category.icon && <span className="text-xl">{category.icon}</span>}
                            <div>
                              <div className="font-medium">{category.display_name}</div>
                              {category.description && (
                                <div className="text-xs text-muted-foreground">{category.description}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {coreFieldsCount > 0 && (
                              <Badge variant="default">
                                {coreFieldsCount} core
                              </Badge>
                            )}
                            {customFieldsCount > 0 && (
                              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                                {customFieldsCount} custom
                              </Badge>
                            )}
                            {totalFieldsCount === 0 && (
                              <Badge variant="outline">0 fields</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{category.sort_order}</TableCell>
                        <TableCell>
                          <Badge variant={category.is_active ? 'default' : 'secondary'}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditCategory(category)}
                              title="Edit category"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (totalFieldsCount > 0) {
                                  if (!confirm(`This category has ${totalFieldsCount} field(s) assigned. Deleting it will move standard fields back to their default groups. Custom fields will retain their category string but won't be grouped. Continue?`)) {
                                    return;
                                  }
                                } else if (!confirm('Are you sure you want to delete this category?')) {
                                  return;
                                }
                                await membersAPI.deleteFieldCategory(category.id);
                                // Silent refresh — keeps the admin on the
                                // Categories tab at their scroll position
                                // instead of remounting back to Profile Builder.
                                await loadDataSilent();
                              }}
                              title="Delete category"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Unsynced categories from custom fields (not yet in FieldCategory table) */}
                  {Array.from(new Set(
                    customFields.filter(isFieldUnsynced).map(f => f.category!)
                  )).sort().map((catName) => {
                    const fieldCount = customFields.filter(f => f.category === catName && isFieldUnsynced(f)).length;
                    return (
                      <TableRow key={`unsynced_${catName}`} className="bg-blue-50 dark:bg-blue-900/20">
                        <TableCell className="font-mono text-sm text-muted-foreground">
                          {catName.toLowerCase().replace(/\s+/g, '_')}
                          <span className="text-xs ml-2">(not synced)</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-xl">📋</span>
                            <div>
                              <div className="font-medium">{catName}</div>
                              <div className="text-xs text-blue-600 dark:text-blue-400">
                                From Custom Field Configuration
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                            {fieldCount} custom field{fieldCount !== 1 ? 's' : ''}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">—</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-blue-700 border-blue-300">
                            Unsynced
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                            onClick={async () => {
                              try {
                                const newCategory = await membersAPI.createFieldCategory({
                                  category_key: catName.toLowerCase().replace(/\s+/g, '_'),
                                  display_name: catName,
                                  description: `Imported from Custom Field Configuration - ${catName}`,
                                  icon: '📋',
                                  sort_order: 50,
                                  is_collapsed_by_default: false,
                                });
                                await loadDataSilent();
                                // Open edit dialog so user can customize icon and sort order
                                setEditingCategory(newCategory);
                                setCategoryForm({
                                  category_key: newCategory.category_key,
                                  display_name: newCategory.display_name,
                                  description: newCategory.description || '',
                                  icon: newCategory.icon || '📋',
                                  sort_order: newCategory.sort_order,
                                  is_collapsed_by_default: newCategory.is_collapsed_by_default,
                                });
                                setShowCategoryDialog(true);
                              } catch (err: any) {
                                setError(err.response?.data?.detail || err.message || 'Failed to sync category');
                              }
                            }}
                            title="Sync this category"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Sync
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {categories.length === 0 && unsyncedCategoryCount === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium">No Custom Categories</p>
                        <p className="text-sm mt-1">Create custom categories to organize member profile fields.</p>
                        <p className="text-xs mt-2 text-muted-foreground">
                          Default groups (Identity, Demographics, Contact, Address, Insurance) are used automatically.
                        </p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unified View Settings Tab */}
        <TabsContent value="unified" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Unified Member View Settings</CardTitle>
              <CardDescription>
                Configure how member profiles are displayed across multiple enrollments and programs.
                These settings ensure one member record is shown consistently across all programs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {unifiedSettings ? (
                <>
                  {/* Sub-mode: org defaults vs per-program overrides */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={visibilityMode === 'org' ? 'default' : 'outline'}
                      onClick={() => setVisibilityMode('org')}
                    >
                      Org defaults
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={visibilityMode === 'program' ? 'default' : 'outline'}
                      onClick={() => setVisibilityMode('program')}
                    >
                      Program overrides
                    </Button>
                  </div>

                  {visibilityMode === 'program' ? (
                    <ProgramOverridesPanel
                      programs={programs}
                      programsLoading={programsLoading}
                      selectedProgramId={selectedProgramId}
                      onSelectProgram={setSelectedProgramId}
                      overrides={programOverrides}
                      overridesLoading={overridesLoading}
                      savingKey={savingOverrideKey}
                      onChange={handleUpdateProgramOverride}
                      tabSections={tabToggleSections}
                    />
                  ) : (
                  <>
                  <div className="grid gap-4">
                    <h4 className="text-sm font-medium">Enrollment Display</h4>
                    <div className="grid gap-3 ml-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Show All Enrollments</Label>
                          <p className="text-sm text-muted-foreground">
                            Display enrollments from all programs on the member profile
                          </p>
                        </div>
                        <Switch
                          checked={unifiedSettings.show_all_enrollments}
                          onCheckedChange={(v) => handleUpdateUnifiedSettings('show_all_enrollments', v)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Show Enrollment History</Label>
                          <p className="text-sm text-muted-foreground">
                            Include completed and historical enrollments
                          </p>
                        </div>
                        <Switch
                          checked={unifiedSettings.show_enrollment_history}
                          onCheckedChange={(v) => handleUpdateUnifiedSettings('show_enrollment_history', v)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Group by Program</Label>
                          <p className="text-sm text-muted-foreground">
                            Organize enrollments by program type
                          </p>
                        </div>
                        <Switch
                          checked={unifiedSettings.group_by_program}
                          onCheckedChange={(v) => handleUpdateUnifiedSettings('group_by_program', v)}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4">
                    <h4 className="text-sm font-medium">Data Preferences</h4>
                    <div className="grid gap-3 ml-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Use Global Member Data</Label>
                          <p className="text-sm text-muted-foreground">
                            Prefer global member record over organization-specific overrides
                          </p>
                        </div>
                        <Switch
                          checked={unifiedSettings.use_global_member_data}
                          onCheckedChange={(v) => handleUpdateUnifiedSettings('use_global_member_data', v)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Highlight Data Conflicts</Label>
                          <p className="text-sm text-muted-foreground">
                            Show warning when data differs between global and org-specific records
                          </p>
                        </div>
                        <Switch
                          checked={unifiedSettings.highlight_data_conflicts}
                          onCheckedChange={(v) => handleUpdateUnifiedSettings('highlight_data_conflicts', v)}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Derived from the server's nav tree, so a tab the profile
                      renders can never again be missing a toggle here. */}
                  {tabToggleSections.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Loading the member-profile tab list…
                    </p>
                  )}
                  {tabToggleSections.map(({ section, tabs }) => (
                    <div className="grid gap-4" key={section}>
                      <h4 className="text-sm font-medium">{section} Section</h4>
                      <div className="grid grid-cols-2 gap-3 ml-4">
                        {tabs.map(({ key, label }) => (
                          <div key={key} className="flex items-center justify-between">
                            <Label>{label}</Label>
                            <Switch
                              checked={unifiedSettings[key as keyof UnifiedViewSettings] as boolean ?? true}
                              onCheckedChange={(v) => handleUpdateUnifiedSettings(key as keyof UnifiedViewSettings, v)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  </>
                  )}
                </>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>Unified view settings not available. Run the database migration first.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
            <DialogDescription>
              Create a custom category to group related fields together in the member profile.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="category_key">Category Key</Label>
              <Input
                id="category_key"
                placeholder="e.g., mailing_address"
                value={categoryForm.category_key}
                onChange={(e) => setCategoryForm({ ...categoryForm, category_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                disabled={!!editingCategory}
              />
              <p className="text-xs text-muted-foreground">
                Internal identifier (lowercase, no spaces)
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                placeholder="e.g., Mailing Address"
                value={categoryForm.display_name}
                onChange={(e) => setCategoryForm({ ...categoryForm, display_name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description..."
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="icon">Icon</Label>
                <Select
                  value={categoryForm.icon || ''}
                  onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an icon">
                      {categoryForm.icon && (
                        <span className="flex items-center gap-2">
                          <span className="text-xl">{categoryForm.icon}</span>
                          <span className="text-sm text-muted-foreground">
                            {CATEGORY_EMOJI_OPTIONS.find(e => e.value === categoryForm.icon)?.label || 'Custom'}
                          </span>
                        </span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_EMOJI_OPTIONS.map((emoji) => (
                      <SelectItem key={emoji.value} value={emoji.value}>
                        <span className="flex items-center gap-2">
                          <span className="text-xl">{emoji.value}</span>
                          <span>{emoji.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={categoryForm.sort_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="collapsed"
                checked={categoryForm.is_collapsed_by_default}
                onCheckedChange={(v) => setCategoryForm({ ...categoryForm, is_collapsed_by_default: v })}
              />
              <Label htmlFor="collapsed">Collapsed by default</Label>
            </div>
          </div>
          <DialogFooter>
            {editingCategory && (
              <Button
                variant="outline"
                className="mr-auto text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={async () => {
                  const uvGroup = unifiedCategoryView.find(g => g.key === `custom_${editingCategory.id}`);
                  const totalFieldsCount = (uvGroup?.fields || []).length;
                  const message = totalFieldsCount > 0
                    ? `This category has ${totalFieldsCount} field(s) assigned. Deleting it will move standard fields back to their default groups. Custom fields will retain their category string but won't be grouped. Continue?`
                    : 'Are you sure you want to delete this category?';
                  if (!confirm(message)) return;
                  setShowCategoryDialog(false);
                  resetCategoryForm();
                  await membersAPI.deleteFieldCategory(editingCategory.id);
                  await loadDataSilent();
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => { setShowCategoryDialog(false); resetCategoryForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={saving || !categoryForm.category_key || !categoryForm.display_name}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Configuration Dialog — TRUS-100 part 1. Rendered as a
          navigable, fully-scrollable editor "page": a sticky header with a
          "← Back" button, a scrolling body (so tall content like the allowed-
          values editor is always reachable), and a sticky footer that keeps
          Save in view. Previously the dialog grew taller than the viewport
          with no scroll, so the Save button at the bottom was unreachable. */}
      <Dialog open={showFieldConfigDialog} onOpenChange={setShowFieldConfigDialog}>
        <DialogContent className="max-w-lg p-0 gap-0 max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
            <button
              type="button"
              onClick={() => { setShowFieldConfigDialog(false); resetConfigForm(); }}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 w-fit"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to fields
            </button>
            <DialogTitle>
              Configure Field: {selectedCoreField?.default_display_name}
            </DialogTitle>
            <DialogDescription>
              Customize how this field appears in the member profile.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="grid gap-2">
              <Label htmlFor="field_display_name">Display Name</Label>
              <Input
                id="field_display_name"
                placeholder={selectedCoreField?.default_display_name}
                value={configForm.display_name}
                onChange={(e) => setConfigForm({ ...configForm, display_name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use default: "{selectedCoreField?.default_display_name}"
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={configForm.category_id?.toString() || ''}
                onValueChange={(v) => setConfigForm({ ...configForm, category_id: v ? parseInt(v) : undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Use default category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Use default ({selectedCoreField?.field_group})</SelectItem>
                  {categories.filter(c => c.is_active).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.icon} {cat.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Applies to — which record type(s) this field is placed on. Every option
                here has a real value store and a real surface behind it; leaving the
                selection empty inherits the underlying field's scope, which is
                members + contacts. */}
            <div className="grid gap-2">
              <Label>Applies to</Label>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-border p-3">
                {RECORD_TYPE_SCOPES.filter((sc) => sc.assignable).map((sc) => (
                  <label
                    key={sc.value}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                    title={sc.hint}
                  >
                    <Checkbox
                      checked={effectiveScope.includes(sc.value)}
                      onCheckedChange={() => toggleConfigScope(sc.value)}
                    />
                    {sc.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Nothing selected means this field follows its default — members and contacts.
              </p>
            </div>
            {/* Read-only. The column has existed on the config row all along but was never
                exposed, so the value was set on 0 of 1192 rows. Surfaced here because the
                cross-CBO field structure spec asks for an editable/read-only toggle per
                field. */}
            <div className="grid gap-2">
              <Label>Editing</Label>
              <label className="flex items-center gap-2 cursor-pointer text-sm rounded-md border border-border p-3">
                <Checkbox
                  checked={!!configForm.is_readonly}
                  onCheckedChange={(v) => setConfigForm({ ...configForm, is_readonly: !!v })}
                />
                Read-only — show the value on the profile but don't let staff change it
              </label>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="placeholder">Placeholder Text</Label>
              <Input
                id="placeholder"
                placeholder="Enter placeholder text..."
                value={configForm.placeholder_text}
                onChange={(e) => setConfigForm({ ...configForm, placeholder_text: e.target.value })}
              />
            </div>

            {/* Edit allowed values — TRUS-100 part 4. Shown for enum-typed core
                fields (gender, race, ethnicity, preferred_language) and for
                select/radio-type custom fields. Lets the org customize the
                option list (add / edit label+value / remove / reorder). */}
            {fieldSupportsOptions(selectedCoreField?.field_type) && (
              <div className="grid gap-2 rounded-md border border-border p-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Settings2 className="h-4 w-4" />
                    Edit allowed values
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {(configForm.field_options || []).length} option{(configForm.field_options || []).length === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Values shown in this field&apos;s dropdown for your organization. Leave the list as-is to inherit the system defaults.
                </p>
                {selectedCoreField?.field_key === 'preferred_language' && (
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Tip: English and Spanish lead the language list — keep them at the top so they appear first.
                  </p>
                )}
                <div className="space-y-2">
                  {(configForm.field_options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => moveFieldOption(idx, 'up')}
                          disabled={idx === 0}
                          title="Move up"
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveFieldOption(idx, 'down')}
                          disabled={idx === (configForm.field_options || []).length - 1}
                          title="Move down"
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <Input
                        placeholder="Value"
                        value={opt.value}
                        onChange={(e) => {
                          const updated: FieldOption[] = [...(configForm.field_options || [])];
                          updated[idx] = { ...updated[idx], value: e.target.value };
                          setConfigForm({ ...configForm, field_options: updated });
                        }}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Display Label"
                        value={opt.label}
                        onChange={(e) => {
                          const updated: FieldOption[] = [...(configForm.field_options || [])];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          setConfigForm({ ...configForm, field_options: updated });
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updated = (configForm.field_options || []).filter((_, i) => i !== idx);
                          setConfigForm({ ...configForm, field_options: updated });
                        }}
                        title="Remove value"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  {(configForm.field_options || []).length === 0 && (
                    <p className="text-xs text-muted-foreground italic">
                      No options configured yet. Add one below or load the effective list.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated: FieldOption[] = [
                        ...(configForm.field_options || []),
                        { value: '', label: '' },
                      ];
                      setConfigForm({ ...configForm, field_options: updated });
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Value
                  </Button>
                  {/* Core enum fields can pull the effective list (org override
                      or system default) straight from the server. */}
                  {selectedCoreField?.field_type === 'enum' && selectedCoreField?.field_key && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={loadingFieldOptions}
                      onClick={() => loadEffectiveFieldOptions(selectedCoreField!.field_key)}
                      title="Load the current effective options (your org override, or the system default)"
                    >
                      {loadingFieldOptions ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      Load current list
                    </Button>
                  )}
                  {selectedCoreField?.default_field_options &&
                    selectedCoreField.default_field_options.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setConfigForm({
                            ...configForm,
                            field_options: selectedCoreField?.default_field_options ?? null,
                          });
                        }}
                        title="Replace this list with the system defaults"
                      >
                        Reset to defaults
                      </Button>
                    )}
                </div>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="help_text">Help Text</Label>
              <Textarea
                id="help_text"
                placeholder="Optional help text shown below the field..."
                value={configForm.help_text}
                onChange={(e) => setConfigForm({ ...configForm, help_text: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="display_width">Display Width</Label>
                <Select
                  value={configForm.display_width}
                  onValueChange={(v) => setConfigForm({ ...configForm, display_width: v as DisplayWidth })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DISPLAY_WIDTHS.map((w) => (
                      <SelectItem key={w.value} value={w.value}>
                        {w.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="field_sort_order">Sort Order</Label>
                <Input
                  id="field_sort_order"
                  type="number"
                  value={configForm.sort_order}
                  onChange={(e) => setConfigForm({ ...configForm, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_hidden"
                  checked={configForm.is_hidden}
                  onCheckedChange={(v) => setConfigForm({ ...configForm, is_hidden: v })}
                />
                <Label htmlFor="is_hidden">Hidden</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_required"
                  checked={configForm.is_required}
                  onCheckedChange={(v) => setConfigForm({ ...configForm, is_required: v })}
                />
                <Label htmlFor="is_required">Required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_readonly"
                  checked={configForm.is_readonly}
                  onCheckedChange={(v) => setConfigForm({ ...configForm, is_readonly: v })}
                />
                <Label htmlFor="is_readonly">Read-only</Label>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-background">
            {editingCustomFieldId && (
              <Button
                variant="outline"
                className="mr-auto text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={async () => {
                  const fieldName = selectedCoreField?.default_display_name || 'this field';
                  const confirmed = window.confirm(
                    `Delete custom field "${fieldName}"? This cannot be undone and will remove all data stored in this field.`
                  );
                  if (!confirmed) return;
                  await membersAPI.deleteCustomField(editingCustomFieldId);
                  await loadDataSilent();
                  setShowFieldConfigDialog(false);
                  resetConfigForm();
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Field
              </Button>
            )}
            <Button variant="outline" onClick={() => { setShowFieldConfigDialog(false); resetConfigForm(); }}>
              Cancel
            </Button>
            {editingConfig && (
              <Button
                variant="destructive"
                onClick={async () => {
                  await membersAPI.deleteFieldConfiguration(editingConfig.id);
                  await loadDataSilent();
                  setShowFieldConfigDialog(false);
                  resetConfigForm();
                }}
              >
                Reset to Default
              </Button>
            )}
            <Button onClick={handleSaveFieldConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Field to Category Dialog */}
      <Dialog open={showAssignFieldDialog} onOpenChange={setShowAssignFieldDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5" />
              Assign Field to Category
            </DialogTitle>
            <DialogDescription>
              Move "{selectedFieldForAssignment && ('default_display_name' in selectedFieldForAssignment
                ? selectedFieldForAssignment.default_display_name
                : selectedFieldForAssignment.display_name)}" to a custom category.
              This will group it with other fields in that category on the member profile.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {categories.filter(c => c.is_active).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No Custom Categories</p>
                <p className="text-sm mt-1">Create a custom category first to assign fields.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setShowAssignFieldDialog(false);
                    setShowCategoryDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Category
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Select Category</Label>
                <div className="grid gap-2 mt-2">
                  {categories.filter(c => c.is_active).map(category => (
                    <Button
                      key={category.id}
                      variant="outline"
                      className="justify-start h-auto py-3"
                      onClick={() => {
                        if (selectedFieldForAssignment) {
                          handleAssignFieldToCategory(selectedFieldForAssignment, category.id);
                        }
                      }}
                      disabled={saving}
                    >
                      <span className="text-xl mr-3">{category.icon || '📁'}</span>
                      <div className="text-left">
                        <div className="font-medium">{category.display_name}</div>
                        {category.description && (
                          <div className="text-xs text-muted-foreground">{category.description}</div>
                        )}
                      </div>
                      {saving && <div className="ml-auto animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAssignFieldDialog(false);
                setSelectedFieldForAssignment(null);
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Program overrides panel (tri-state per-tab visibility)
// ============================================================================

interface ProgramOverridesPanelProps {
  programs: Program[];
  programsLoading: boolean;
  selectedProgramId: number | null;
  onSelectProgram: (id: number | null) => void;
  overrides: Record<string, boolean>;
  overridesLoading: boolean;
  savingKey: string | null;
  onChange: (tabKey: string, state: OverrideState) => void;
  /** Toggle rows derived from the server nav tree; see deriveTabToggleSections. */
  tabSections: { section: string; tabs: { key: string; label: string }[] }[];
}

function overrideStateFor(
  overrides: Record<string, boolean>,
  key: string,
): OverrideState {
  if (!(key in overrides)) return 'inherit';
  return overrides[key] ? 'show' : 'hide';
}

// ============================================================================
// Profile Layout editor — per-org terminology and ordering (TRUS-528)
// ============================================================================

type LayoutDraft = Record<string, { label?: string | null; order?: number; placement?: ProfilePlacement }>;

const draftKey = (scope: 'section' | 'tab', elementKey: string) => `${scope}:${elementKey}`;

interface ProfileLayoutEditorProps {
  layout: ProfileLayout | null;
  saving: boolean;
  onSave: (overrides: ProfileLayoutOverrideInput[]) => Promise<void>;
  onReset: () => Promise<void>;
}

/**
 * Renames and reorders the member-profile nav rail for this organization.
 *
 * Reordering is up/down buttons, not drag-and-drop, even though this file uses
 * dnd-kit for the field lists. Two reasons: the lists here are short (6
 * sections, at most 8 items each), and a drag whose persisted order is not the
 * order the list re-sorts by snaps back silently — the exact bug the Profile
 * Builder shipped with. Buttons make the written value and the rendered order
 * the same thing.
 *
 * Moving one element renumbers its whole sibling group in tens and marks them
 * all dirty. Writing an explicit order for every sibling is what stops a later
 * platform change to one default from reshuffling an org's arrangement around
 * it.
 */
const ProfileLayoutEditor: React.FC<ProfileLayoutEditorProps> = ({ layout, saving, onSave, onReset }) => {
  const { t } = useAppTranslation();
  const [draft, setDraft] = useState<LayoutDraft>({});

  // Drop the draft whenever a fresh payload arrives (save, reset, reload):
  // keeping it would replay edits over values the server has already applied.
  useEffect(() => { setDraft({}); }, [layout]);

  const dirtyCount = Object.keys(draft).length;

  const effectiveLabel = (scope: 'section' | 'tab', el: { element_key: string; label_override: string | null }) => {
    const entry = draft[draftKey(scope, el.element_key)];
    return entry && entry.label !== undefined ? (entry.label ?? '') : (el.label_override ?? '');
  };

  const setLabel = (scope: 'section' | 'tab', elementKey: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [draftKey(scope, elementKey)]: { ...prev[draftKey(scope, elementKey)], label: value },
    }));
  };

  const orderOf = (scope: 'section' | 'tab', el: { element_key: string; display_order: number }) =>
    draft[draftKey(scope, el.element_key)]?.order ?? el.display_order;

  // TRUS-762 round 4: where a top-level tab is listed — the sidebar, or behind
  // the Member setup gear. Persisted as the override's `placement`.
  const placementOf = (tab: ProfileLayoutTab): ProfilePlacement =>
    draft[draftKey('tab', tab.element_key)]?.placement ?? tab.placement ?? tab.default_placement ?? 'rail';

  const setPlacement = (tab: ProfileLayoutTab, value: ProfilePlacement) => {
    setDraft((prev) => ({
      ...prev,
      [draftKey('tab', tab.element_key)]: { ...prev[draftKey('tab', tab.element_key)], placement: value },
    }));
  };

  const move = (
    scope: 'section' | 'tab',
    siblings: { element_key: string; display_order: number }[],
    index: number,
    delta: number,
  ) => {
    const target = index + delta;
    if (target < 0 || target >= siblings.length) return;
    const ordered = [...siblings].sort((a, b) => orderOf(scope, a) - orderOf(scope, b));
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    setDraft((prev) => {
      const next = { ...prev };
      ordered.forEach((el, i) => {
        const key = draftKey(scope, el.element_key);
        next[key] = { ...next[key], order: (i + 1) * 10 };
      });
      return next;
    });
  };

  const handleSave = async () => {
    const overrides: ProfileLayoutOverrideInput[] = Object.entries(draft).map(([key, value]) => {
      const [scope, ...rest] = key.split(':');
      return {
        scope: scope as 'section' | 'tab',
        element_key: rest.join(':'),
        // "" is how an admin clears a rename; the server reads it as "back to
        // the platform default", not as an empty tab name.
        label_override: value.label === undefined ? undefined : (value.label || null),
        display_order: value.order,
        // Omitted = keep the org's placement. Choosing the platform default
        // stores null, so a later change to that default reaches this org.
        ...(value.placement === undefined
          ? {}
          : { placement: value.placement === defaultPlacementOf(rest.join(':')) ? null : value.placement }),
      };
    });
    await onSave(overrides);
  };

  function defaultPlacementOf(elementKey: string): ProfilePlacement | undefined {
    for (const section of layout?.sections ?? []) {
      const tab = section.tabs.find((tb) => tb.element_key === elementKey);
      if (tab) return tab.default_placement;
    }
    return undefined;
  }

  if (!layout) {
    return <p className="text-sm text-muted-foreground">Loading the member-profile layout…</p>;
  }

  const sections = [...layout.sections].sort((a, b) => orderOf('section', a) - orderOf('section', b));

  const renderTabRow = (
    tab: ProfileLayoutTab,
    siblings: ProfileLayoutTab[],
    index: number,
    depth: number,
  ) => (
    <div key={tab.element_key}>
      <div
        className="flex items-center gap-2 py-1.5"
        style={{ paddingLeft: `${depth * 1.25}rem` }}
      >
        <div className="flex flex-col">
          <button
            type="button"
            aria-label={`Move ${tab.default_label} up`}
            disabled={index === 0 || saving}
            onClick={() => move('tab', siblings, index, -1)}
            className="px-1 text-xs text-muted-foreground disabled:opacity-30 hover:text-foreground"
          >
            ▲
          </button>
          <button
            type="button"
            aria-label={`Move ${tab.default_label} down`}
            disabled={index === siblings.length - 1 || saving}
            onClick={() => move('tab', siblings, index, 1)}
            className="px-1 text-xs text-muted-foreground disabled:opacity-30 hover:text-foreground"
          >
            ▼
          </button>
        </div>
        <Input
          value={effectiveLabel('tab', tab)}
          placeholder={tab.default_label}
          disabled={saving || tab.rename_via === 'note_categories'}
          onChange={(e) => setLabel('tab', tab.element_key, e.target.value)}
          className="max-w-xs"
        />
        <span className="text-xs text-muted-foreground">{tab.default_label}</span>
        {depth === 0 && tab.placement_overridable && (
          <div className="flex items-center gap-1.5" data-testid={`placement-${tab.element_key}`}>
            <span className="text-xs text-muted-foreground">{t('profileLayout.show_in', 'Show in')}</span>
            <Select
              value={placementOf(tab)}
              onValueChange={(v) => setPlacement(tab, v as ProfilePlacement)}
              disabled={saving}
            >
              <SelectTrigger
                className="h-7 w-36 text-xs"
                aria-label={t('profileLayout.show_in_for', 'Show {{name}} in', { name: tab.default_label })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rail">{t('profileLayout.placement_rail', 'Sidebar')}</SelectItem>
                <SelectItem value="setup">{t('profileLayout.placement_setup', 'Member setup')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {tab.pinned_form_id != null && (
          <span className="rounded bg-v2accent/10 px-1.5 py-0.5 text-[10px] text-v2accent">
            {t('profileLayout.pinned_form', 'pinned form')}
          </span>
        )}
        {!tab.visible && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">hidden</span>
        )}
        {tab.data_gated && (
          <span
            className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700"
            title="Shown automatically when the member's data or your capabilities call for it — there is no on/off switch."
          >
            automatic
          </span>
        )}
        {tab.rename_via === 'note_categories' && (
          <span className="text-[10px] text-muted-foreground">
            rename this one under Categories
          </span>
        )}
      </div>
      {(tab.sub_tabs ?? []).length > 0 && (
        <div>
          {[...(tab.sub_tabs ?? [])]
            .sort((a, b) => orderOf('tab', a) - orderOf('tab', b))
            .map((sub, i, arr) => renderTabRow(sub, arr, i, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        Rename the member-profile sections and tabs to match the words your
        organization uses, and put them in the order your staff work in. Leave a
        box empty to keep the standard name. This changes wording and order only
        — use <strong>Unified View</strong> above to show or hide a tab, and{' '}
        <strong>Program Overrides</strong> to vary that by program.
      </div>

      {sections.map((section, si) => {
        const tabs = [...section.tabs].sort((a, b) => orderOf('tab', a) - orderOf('tab', b));
        return (
          <div key={section.element_key} className="rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <button
                  type="button"
                  aria-label={`Move ${section.default_label} up`}
                  disabled={si === 0 || saving}
                  onClick={() => move('section', sections, si, -1)}
                  className="px-1 text-xs text-muted-foreground disabled:opacity-30 hover:text-foreground"
                >
                  ▲
                </button>
                <button
                  type="button"
                  aria-label={`Move ${section.default_label} down`}
                  disabled={si === sections.length - 1 || saving}
                  onClick={() => move('section', sections, si, 1)}
                  className="px-1 text-xs text-muted-foreground disabled:opacity-30 hover:text-foreground"
                >
                  ▼
                </button>
              </div>
              <Input
                value={effectiveLabel('section', section)}
                placeholder={section.default_label}
                disabled={saving}
                onChange={(e) => setLabel('section', section.element_key, e.target.value)}
                className="max-w-xs font-medium"
              />
              <span className="text-xs text-muted-foreground">{section.default_label}</span>
            </div>
            <Separator className="my-2" />
            <div>{tabs.map((tab, i) => renderTabRow(tab, tabs, i, 0))}</div>
          </div>
        );
      })}

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving || dirtyCount === 0}>
          {saving ? 'Saving…' : `Save${dirtyCount ? ` (${dirtyCount})` : ''}`}
        </Button>
        <Button variant="outline" onClick={onReset} disabled={saving}>
          Reset to standard names and order
        </Button>
        {dirtyCount > 0 && (
          <span className="text-xs text-muted-foreground">Unsaved changes</span>
        )}
      </div>
    </div>
  );
};

const ProgramOverridesPanel: React.FC<ProgramOverridesPanelProps> = ({
  programs,
  programsLoading,
  selectedProgramId,
  onSelectProgram,
  overrides,
  overridesLoading,
  savingKey,
  onChange,
  tabSections,
}) => {
  const OPTIONS: { value: OverrideState; label: string }[] = [
    { value: 'inherit', label: 'Inherit' },
    { value: 'show', label: 'Show' },
    { value: 'hide', label: 'Hide' },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        Program overrides let a tab appear (or hide) only for members enrolled in
        a specific program. <strong>Inherit</strong> falls back to the org default
        above. When a member is in multiple programs, overrides are{' '}
        <strong>most-permissive</strong>: if any of the member&apos;s programs
        shows a tab, the member sees it.
      </div>

      <div className="max-w-sm">
        <Label htmlFor="program-override-select">Program</Label>
        <Select
          value={selectedProgramId != null ? String(selectedProgramId) : ''}
          onValueChange={(v) => onSelectProgram(v ? parseInt(v, 10) : null)}
          disabled={programsLoading}
        >
          <SelectTrigger id="program-override-select" className="mt-1">
            <SelectValue
              placeholder={programsLoading ? 'Loading programs…' : 'Select a program'}
            />
          </SelectTrigger>
          <SelectContent>
            {programs.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedProgramId == null ? (
        <p className="text-sm text-muted-foreground">
          Select a program to configure its tab overrides.
        </p>
      ) : overridesLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading overrides…
        </div>
      ) : (
        <div className="space-y-5">
          {tabSections.map(({ section, tabs }) => (
            <div key={section} className="grid gap-3">
              <h4 className="text-sm font-medium">{section} Section</h4>
              <div className="grid gap-2 ml-4">
                {tabs.map(({ key, label }) => {
                  const state = overrideStateFor(overrides, key);
                  return (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <Label className="flex items-center gap-2">
                        {label}
                        {savingKey === key && (
                          <RefreshCw
                            className="h-3 w-3 animate-spin text-muted-foreground"
                            aria-hidden="true"
                          />
                        )}
                      </Label>
                      {/* Tri-state segmented control */}
                      <div
                        role="radiogroup"
                        aria-label={`${label} visibility`}
                        className="inline-flex overflow-hidden rounded-md border"
                      >
                        {OPTIONS.map((opt) => {
                          const active = state === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              disabled={savingKey === key}
                              onClick={() => onChange(key, opt.value)}
                              className={`px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                                active
                                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                                  : 'bg-transparent text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
